import React, { useState, useEffect, useRef, useMemo } from 'react';

// Add this CSS at the top of your component
const soundWaveStyles = `
  @keyframes tvTurnOn {
    0% {
      clip-path: polygon(50% 49.9%, 50% 50.1%, 50% 50.1%, 50% 49.9%);
      opacity: 1;
      filter: brightness(2);
    }
    30% {
      clip-path: polygon(0% 49.5%, 100% 49.5%, 100% 50.5%, 0% 50.5%);
      filter: brightness(1.8);
    }
    60% {
      clip-path: polygon(0% 30%, 100% 30%, 100% 70%, 0% 70%);
      filter: brightness(1.5);
    }
    85% {
      clip-path: polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%);
      filter: brightness(1.2);
    }
    100% {
      clip-path: polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%);
      opacity: 0;
      filter: brightness(1);
    }
  }

  @keyframes staticFade {
    0% { 
      opacity: 0.9;
      background-position: 0 0;
    }
    25% { 
      opacity: 0.7;
      background-position: -10px -5px;
    }
    50% { 
      opacity: 0.5;
      background-position: 5px -10px;
    }
    75% { 
      opacity: 0.3;
      background-position: -5px 5px;
    }
    100% { 
      opacity: 0;
      background-position: 0 0;
    }
  }

  @keyframes terminalBoot {
    0% {
      opacity: 0;
      filter: brightness(0);
    }
    50% {
      opacity: 0.5;
      filter: brightness(0.5);
    }
    100% {
      opacity: 1;
      filter: brightness(1);
    }
  }

  @keyframes powerOn {
    0% {
      opacity: 0;
      filter: brightness(0) contrast(0);
    }
    20% {
      opacity: 0.3;
      filter: brightness(0.2) contrast(0.5);
    }
    40% {
      opacity: 0.6;
      filter: brightness(0.5) contrast(0.8);
    }
    60% {
      opacity: 0.8;
      filter: brightness(0.8) contrast(1);
    }
    80% {
      opacity: 0.9;
      filter: brightness(1.2) contrast(1.2);
    }
    100% {
      opacity: 1;
      filter: brightness(1) contrast(1);
    }
  }

  .tv-boot-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100vw;
    height: 100vh;
    min-height: 100vh;
    min-width: 100vw;
    background: #000;
    z-index: 9999;
    pointer-events: none;
    overflow: hidden;
  }

  .tv-turn-on-effect {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    background: #00ff00;
    animation: tvTurnOn 1.8s ease-out forwards;
    z-index: 2;
  }

  .tv-static {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100vw;
    height: 100vh;
    min-width: 100vw;
    min-height: 100vh;
    background: 
      radial-gradient(circle, transparent 30%, rgba(255,255,255,0.1) 31%, rgba(255,255,255,0.1) 32%, transparent 33%),
      linear-gradient(0deg, transparent 49%, rgba(255,255,255,0.03) 50%, rgba(255,255,255,0.03) 51%, transparent 52%);
    background-size: 4px 4px, 100% 3px;
    animation: staticFade 2.8s ease-out forwards;
  }

  .terminal-container.booting {
    animation: terminalBoot 1.5s ease-out forwards;
    animation-delay: 0.3s;
    opacity: 0;
  }

  @keyframes soundPulse {
    0% { opacity: 0.2; }
    50% { opacity: 1; }
    100% { opacity: 0.2; }
  }

  @keyframes pulse {
    0% {
      opacity: 1;
      text-shadow: 0 0 10px #00ff00, 0 0 20px #00ff00;
    }
    50% {
      opacity: 0.8;
      text-shadow: 0 0 15px #00ff00, 0 0 30px #00ff00, 0 0 40px #00ff00;
    }
    100% {
      opacity: 1;
      text-shadow: 0 0 10px #00ff00, 0 0 20px #00ff00;
    }
  }

  @keyframes buttonGlow {
    0% {
      box-shadow: 0 0 8px #00ff00,
                  0 0 12px #00ff00,
                  0 0 16px rgba(0, 255, 0, 0.6),
                  inset 0 0 6px rgba(0, 255, 0, 0.08);
      border-color: #00ff00;
      opacity: 0.9;
    }
    25% {
      box-shadow: 0 0 10px #00ff00,
                  0 0 16px #00ff00,
                  0 0 20px rgba(0, 255, 0, 0.7),
                  inset 0 0 8px rgba(0, 255, 0, 0.1);
      border-color: #00ff22;
      opacity: 0.95;
    }
    50% {
      box-shadow: 0 0 12px #00ff00,
                  0 0 20px #00ff00,
                  0 0 24px rgba(0, 255, 0, 0.8),
                  inset 0 0 10px rgba(0, 255, 0, 0.12);
      border-color: #00ff44;
      opacity: 1;
    }
    75% {
      box-shadow: 0 0 10px #00ff00,
                  0 0 16px #00ff00,
                  0 0 20px rgba(0, 255, 0, 0.7),
                  inset 0 0 8px rgba(0, 255, 0, 0.1);
      border-color: #00ff22;
      opacity: 0.95;
    }
    100% {
      box-shadow: 0 0 8px #00ff00,
                  0 0 12px #00ff00,
                  0 0 16px rgba(0, 255, 0, 0.6),
                  inset 0 0 6px rgba(0, 255, 0, 0.08);
      border-color: #00ff00;
      opacity: 0.9;
    }
  }

  .start-button-glow {
    animation: buttonGlow 5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
    border: 2px solid #00ff00;
    transition: all 1s cubic-bezier(0.4, 0, 0.6, 1);
    position: relative;
    overflow: hidden;
  }

  .start-button-glow:hover {
    animation: buttonGlow 4s cubic-bezier(0.4, 0, 0.6, 1) infinite;
    background-color: rgba(0, 255, 0, 0.03);
    transform: scale(1.008);
    box-shadow: 0 0 15px #00ff00,
                0 0 25px #00ff00,
                0 0 35px rgba(0, 255, 0, 0.6),
                inset 0 0 10px rgba(0, 255, 0, 0.1);
  }

  @keyframes glitch {
    0% {
      text-shadow: 0.05em 0 0 #ff0000, -0.05em -0.025em 0 #00ffff,
                   0.025em 0.05em 0 #ffff00;
      transform: translate(0);
    }
    15% {
      text-shadow: 0.05em 0 0 #ff0000, -0.05em -0.025em 0 #00ffff,
                   0.025em 0.05em 0 #ffff00;
      transform: translate(-0.5px, 0.5px);
    }
    20% {
      text-shadow: -0.05em -0.025em 0 #ff0000, 0.025em 0.025em 0 #00ffff,
                   -0.05em -0.05em 0 #ffff00;
      transform: translate(0.5px, -0.5px);
    }
    30% {
      text-shadow: 0.025em 0.05em 0 #ff0000, 0.05em 0 0 #00ffff,
                   0 -0.05em 0 #ffff00;
      transform: translate(-0.3px, 0.3px);
    }
    35% {
      text-shadow: -0.05em -0.025em 0 #ff0000, 0.025em 0.025em 0 #00ffff,
                   -0.025em -0.05em 0 #ffff00;
      transform: translate(0.3px, -0.3px);
    }
    40% {
      text-shadow: -0.025em -0.05em 0 #ff0000, -0.025em 0.025em 0 #00ffff,
                   0.025em -0.05em 0 #ffff00;
      transform: translate(-0.2px, 0.2px);
    }
    50% {
      text-shadow: 0 0.025em 0 #ff0000, -0.05em -0.025em 0 #00ffff,
                   -0.025em 0.05em 0 #ffff00;
      transform: translate(0);
    }
    100% {
      text-shadow: 0.05em 0 0 #ff0000, -0.05em -0.025em 0 #00ffff,
                   0.025em 0.05em 0 #ffff00;
      transform: translate(0);
    }
  }

  .visitor-name-glitch {
    animation: glitch 2.5s infinite;
    position: relative;
    display: inline-block;
    font-weight: bold;
    color: #00ff00;
  }

  @keyframes glitchRed {
    0%, 100% {
      text-shadow: 0.05em 0 0 #ff0000, -0.05em 0 0 #ff3333;
      transform: translate(0);
      filter: hue-rotate(0deg);
    }
    25% {
      text-shadow: -0.05em 0 0 #ff0000, 0.05em 0 0 #ff6666;
      transform: translate(-1px, 0);
      filter: hue-rotate(5deg);
    }
    50% {
      text-shadow: 0 0.05em 0 #ff0000, 0 -0.05em 0 #ff3333;
      transform: translate(0, 1px);
      filter: hue-rotate(10deg);
    }
    75% {
      text-shadow: 0.05em -0.05em 0 #ff0000, -0.05em 0.05em 0 #ff6666;
      transform: translate(1px, -1px);
      filter: hue-rotate(5deg);
    }
  }

  @keyframes glitchBlue {
    0%, 100% {
      text-shadow: 0.03em 0 0 #0066ff, -0.03em 0 0 #3388ff;
      transform: translate(0);
      filter: brightness(1);
    }
    25% {
      text-shadow: -0.03em 0 0 #0066ff, 0.03em 0 0 #66aaff;
      transform: translate(-0.3px, 0.3px);
      filter: brightness(1.2);
    }
    50% {
      text-shadow: 0 0.03em 0 #0066ff, 0 -0.03em 0 #3388ff;
      transform: translate(0.3px, -0.3px);
      filter: brightness(1.4);
    }
    75% {
      text-shadow: 0.03em 0.03em 0 #0066ff, -0.03em -0.03em 0 #66aaff;
      transform: translate(-0.2px, 0.2px);
      filter: brightness(1.1);
    }
  }

  @keyframes glitchPurple {
    0%, 100% {
      text-shadow: 0.08em 0 0 #8800ff, -0.08em 0 0 #aa44ff;
      transform: translate(0);
      filter: saturate(1);
    }
    20% {
      text-shadow: -0.08em 0 0 #8800ff, 0.08em 0 0 #cc66ff;
      transform: translate(-1.5px, 1.5px);
      filter: saturate(1.3);
    }
    40% {
      text-shadow: 0 0.08em 0 #8800ff, 0 -0.08em 0 #aa44ff;
      transform: translate(1.5px, -1.5px);
      filter: saturate(1.6);
    }
    60% {
      text-shadow: 0.08em -0.08em 0 #8800ff, -0.08em 0.08em 0 #cc66ff;
      transform: translate(-1px, 1px);
      filter: saturate(1.4);
    }
    80% {
      text-shadow: -0.08em 0.08em 0 #8800ff, 0.08em -0.08em 0 #aa44ff;
      transform: translate(1px, -1px);
      filter: saturate(1.1);
    }
  }

  .system-name-glitch-1 {
    animation: glitchRed 1.5s infinite;
    display: inline-block;
    color: #ff0000;
    font-weight: bold;
  }

  .system-name-glitch-2 {
    animation: glitchBlue 3s infinite;
    display: inline-block;
    color: #0066ff;
    font-weight: bold;
  }

  .system-name-glitch-3 {
    animation: glitchPurple 1s infinite;
    display: inline-block;
    color: #8800ff;
    font-weight: bold;
  }

  .system-name-glitch-4 {
    animation: glitch 2s infinite;
    display: inline-block;
    color: #00ff00;
    font-weight: bold;
  }

  @keyframes glitchFade {
    0%, 100% {
      opacity: 1;
      text-shadow: 0.04em 0 0 #ff00ff, -0.04em 0 0 #00ffff;
      transform: translate(0);
    }
    20% {
      opacity: 0.8;
      text-shadow: 0.04em 0 0 #ff00ff, -0.04em 0 0 #00ffff;
      transform: translate(-0.5px, 0);
    }
    40% {
      opacity: 0.3;
      text-shadow: -0.04em 0 0 #ff00ff, 0.04em 0 0 #00ffff;
      transform: translate(0.5px, 0);
    }
    60% {
      opacity: 0.1;
      text-shadow: 0 0.04em 0 #ff00ff, 0 -0.04em 0 #00ffff;
      transform: translate(0, -0.5px);
    }
    80% {
      opacity: 0.6;
      text-shadow: -0.02em -0.02em 0 #ff00ff, 0.02em 0.02em 0 #00ffff;
      transform: translate(0, 0.5px);
    }
  }

  @keyframes glitchRainbow {
    0% {
      text-shadow: 0.05em 0 0 #ff0000, -0.05em 0 0 #00ff00;
      filter: hue-rotate(0deg);
    }
    16% {
      text-shadow: 0.05em 0 0 #ff8800, -0.05em 0 0 #0088ff;
      filter: hue-rotate(60deg);
    }
    33% {
      text-shadow: 0.05em 0 0 #ffff00, -0.05em 0 0 #ff00ff;
      filter: hue-rotate(120deg);
    }
    50% {
      text-shadow: 0.05em 0 0 #88ff00, -0.05em 0 0 #ff0088;
      filter: hue-rotate(180deg);
    }
    66% {
      text-shadow: 0.05em 0 0 #00ff88, -0.05em 0 0 #8800ff;
      filter: hue-rotate(240deg);
    }
    83% {
      text-shadow: 0.05em 0 0 #0088ff, -0.05em 0 0 #ff8800;
      filter: hue-rotate(300deg);
    }
    100% {
      text-shadow: 0.05em 0 0 #ff0000, -0.05em 0 0 #00ff00;
      filter: hue-rotate(360deg);
    }
  }

  @keyframes glitchBlur {
    0%, 100% {
      text-shadow: 0.03em 0 0 #ff0066, -0.03em 0 0 #0066ff;
      filter: blur(0px);
      transform: scale(1);
    }
    25% {
      text-shadow: 0.06em 0 0 #ff0066, -0.06em 0 0 #0066ff;
      filter: blur(1px);
      transform: scale(1.02);
    }
    50% {
      text-shadow: 0.09em 0 0 #ff0066, -0.09em 0 0 #0066ff;
      filter: blur(2px);
      transform: scale(0.98);
    }
    75% {
      text-shadow: 0.06em 0 0 #ff0066, -0.06em 0 0 #0066ff;
      filter: blur(1px);
      transform: scale(1.01);
    }
  }

  @keyframes glitchMatrix {
    0%, 100% {
      text-shadow: 0.04em 0 0 #00ff41, -0.04em 0 0 #ff4100, 0 0.04em 0 #4100ff;
      transform: translate(0) skew(0deg);
    }
    10% {
      text-shadow: -0.04em 0 0 #00ff41, 0.04em 0 0 #ff4100, 0 -0.04em 0 #4100ff;
      transform: translate(-1px, 1px) skew(2deg);
    }
    20% {
      text-shadow: 0.04em 0.04em 0 #00ff41, -0.04em -0.04em 0 #ff4100, 0.04em -0.04em 0 #4100ff;
      transform: translate(1px, -1px) skew(-2deg);
    }
    30% {
      text-shadow: -0.04em 0.04em 0 #00ff41, 0.04em -0.04em 0 #ff4100, -0.04em 0 0 #4100ff;
      transform: translate(-0.5px, 0.5px) skew(1deg);
    }
  }

  @keyframes glitchVaporwave {
    0%, 100% {
      text-shadow: 0.05em 0 0 #ff00ff, -0.05em 0 0 #00ffff, 0 0.05em 0 #ffff00;
      filter: hue-rotate(0deg) saturate(1.5);
      transform: translate(0);
    }
    25% {
      text-shadow: -0.05em 0 0 #ff00ff, 0.05em 0 0 #00ffff, 0 -0.05em 0 #ffff00;
      filter: hue-rotate(90deg) saturate(2);
      transform: translate(-1px, 0);
    }
    50% {
      text-shadow: 0 0.05em 0 #ff00ff, 0 -0.05em 0 #00ffff, 0.05em 0 0 #ffff00;
      filter: hue-rotate(180deg) saturate(1.8);
      transform: translate(0, -1px);
    }
    75% {
      text-shadow: 0.05em -0.05em 0 #ff00ff, -0.05em 0.05em 0 #00ffff, -0.05em -0.05em 0 #ffff00;
      filter: hue-rotate(270deg) saturate(2.2);
      transform: translate(1px, 1px);
    }
  }

  @keyframes glitchCorrupted {
    0% {
      text-shadow: 0.02em 0 0 #ff3300, -0.02em 0 0 #3300ff;
      transform: translate(0) scale(1);
      opacity: 1;
    }
    5% {
      text-shadow: 0.1em 0 0 #ff3300, -0.1em 0 0 #3300ff, 0.05em 0.05em 0 #33ff00;
      transform: translate(-2px, 1px) scale(1.1);
      opacity: 0.9;
    }
    10% {
      text-shadow: -0.08em 0 0 #ff3300, 0.08em 0 0 #3300ff, -0.04em -0.04em 0 #33ff00;
      transform: translate(2px, -1px) scale(0.9);
      opacity: 0.8;
    }
    15% {
      text-shadow: 0.06em 0.06em 0 #ff3300, -0.06em -0.06em 0 #3300ff;
      transform: translate(-1px, 1px) scale(1.05);
      opacity: 1;
    }
    100% {
      text-shadow: 0.02em 0 0 #ff3300, -0.02em 0 0 #3300ff;
      transform: translate(0) scale(1);
      opacity: 1;
    }
  }

  @keyframes glitchNeon {
    0%, 100% {
      text-shadow: 0 0 5px #ff0080, 0.03em 0 0 #0080ff, -0.03em 0 0 #80ff00;
      filter: brightness(1);
    }
    20% {
      text-shadow: 0 0 10px #ff0080, 0.06em 0 0 #0080ff, -0.06em 0 0 #80ff00;
      filter: brightness(1.5);
    }
    40% {
      text-shadow: 0 0 15px #ff0080, 0.09em 0 0 #0080ff, -0.09em 0 0 #80ff00;
      filter: brightness(2);
    }
    60% {
      text-shadow: 0 0 20px #ff0080, 0.06em 0 0 #0080ff, -0.06em 0 0 #80ff00;
      filter: brightness(1.8);
    }
    80% {
      text-shadow: 0 0 10px #ff0080, 0.03em 0 0 #0080ff, -0.03em 0 0 #80ff00;
      filter: brightness(1.2);
    }
  }

  .system-name-glitch-5 {
    animation: glitchFade 2.5s infinite;
    display: inline-block;
    color: #ff00ff;
    font-weight: bold;
  }

  .system-name-glitch-6 {
    animation: glitchRainbow 3s infinite;
    display: inline-block;
    color: #00ff00;
    font-weight: bold;
  }

  .system-name-glitch-7 {
    animation: glitchBlur 1.8s infinite;
    display: inline-block;
    color: #ff0066;
    font-weight: bold;
  }

  .system-name-glitch-8 {
    animation: glitchMatrix 2.2s infinite;
    display: inline-block;
    color: #00ff41;
    font-weight: bold;
  }

  .system-name-glitch-9 {
    animation: glitchVaporwave 2.8s infinite;
    display: inline-block;
    color: #ff00ff;
    font-weight: bold;
  }

  .system-name-glitch-10 {
    animation: glitchCorrupted 1.2s infinite;
    display: inline-block;
    color: #ff3300;
    font-weight: bold;
  }

  .system-name-glitch-11 {
    animation: glitchNeon 3.5s infinite;
    display: inline-block;
    color: #ff0080;
    font-weight: bold;
  }

  /* Thematic effects for key concepts */
  @keyframes wealthyGlow {
    0%, 100% {
      color: #ff1744;
      text-shadow: 0 0 8px #ff1744, 0 0 16px #e91e63, 0 0 24px #9c27b0;
      filter: brightness(1) saturate(1.5) hue-rotate(0deg);
      transform: rotate(0deg) scale(1);
    }
    20% {
      color: #ff4081;
      text-shadow: 0 0 10px #ff4081, 0 0 18px #e91e63, 0 0 26px #9c27b0, 0 0 34px #3f51b5;
      filter: brightness(1.1) saturate(1.6) hue-rotate(60deg);
      transform: rotate(2deg) scale(1.02);
    }
    40% {
      color: #e040fb;
      text-shadow: 0 0 12px #e040fb, 0 0 20px #9c27b0, 0 0 28px #3f51b5, 0 0 36px #2196f3;
      filter: brightness(1.2) saturate(1.7) hue-rotate(120deg);
      transform: rotate(0deg) scale(1.04);
    }
    60% {
      color: #7c4dff;
      text-shadow: 0 0 14px #7c4dff, 0 0 22px #3f51b5, 0 0 30px #2196f3, 0 0 38px #00bcd4;
      filter: brightness(1.3) saturate(1.8) hue-rotate(180deg);
      transform: rotate(-2deg) scale(1.03);
    }
    80% {
      color: #00e676;
      text-shadow: 0 0 12px #00e676, 0 0 20px #4caf50, 0 0 28px #ffeb3b, 0 0 36px #ff9800;
      filter: brightness(1.2) saturate(1.6) hue-rotate(240deg);
      transform: rotate(1deg) scale(1.02);
    }
  }

  @keyframes richEffect {
    0%, 100% {
      color: #ffd700;
      text-shadow: 0 0 8px #ffd700, 0 0 16px #ffb300, 0 0 24px #ff8f00;
      transform: scale(1) skew(0deg);
      filter: brightness(1) contrast(1);
    }
    20% {
      color: #ffecb3;
      text-shadow: 0 0 12px #ffd700, 0 0 20px #ffb300, 0 0 28px #ff8f00, 0 0 36px #e65100;
      transform: scale(1.03) skew(1deg);
      filter: brightness(1.1) contrast(1.1);
    }
    40% {
      color: #fff9c4;
      text-shadow: 0 0 16px #ffd700, 0 0 24px #ffb300, 0 0 32px #ff8f00, 0 0 40px #e65100, 0 0 48px #bf360c;
      transform: scale(1.06) skew(0deg);
      filter: brightness(1.2) contrast(1.2);
    }
    60% {
      color: #fffde7;
      text-shadow: 0 0 20px #ffd700, 0 0 28px #ffb300, 0 0 36px #ff8f00, 0 0 44px #e65100, 0 0 52px #bf360c;
      transform: scale(1.08) skew(-1deg);
      filter: brightness(1.3) contrast(1.3);
    }
    80% {
      color: #fff9c4;
      text-shadow: 0 0 16px #ffd700, 0 0 24px #ffb300, 0 0 32px #ff8f00, 0 0 40px #e65100, 0 0 48px #bf360c;
      transform: scale(1.04) skew(0deg);
      filter: brightness(1.1) contrast(1.1);
    }
  }

  @keyframes financiallySecurePulse {
    0%, 100% {
      color: #4caf50;
      text-shadow: 0 0 8px #4caf50, 0 0 16px #8bc34a, 0 0 24px #cddc39;
      filter: hue-rotate(0deg) saturate(1);
      transform: scale(1);
    }
    25% {
      color: #66bb6a;
      text-shadow: 0 0 10px #4caf50, 0 0 18px #8bc34a, 0 0 26px #cddc39, 0 0 34px #ffeb3b;
      filter: hue-rotate(5deg) saturate(1.1);
      transform: scale(1.01);
    }
    50% {
      color: #81c784;
      text-shadow: 0 0 12px #4caf50, 0 0 20px #8bc34a, 0 0 28px #cddc39, 0 0 36px #ffeb3b, 0 0 44px #00e676;
      filter: hue-rotate(10deg) saturate(1.2);
      transform: scale(1.02);
    }
    75% {
      color: #a5d6a7;
      text-shadow: 0 0 14px #4caf50, 0 0 22px #8bc34a, 0 0 30px #cddc39, 0 0 38px #ffeb3b, 0 0 46px #00e676;
      filter: hue-rotate(15deg) saturate(1.3);
      transform: scale(1.01);
    }
  }

  @keyframes priorityEmphasis {
    0%, 100% {
      color: #e91e63;
      text-shadow: 0 0 8px #e91e63, 0 0 16px #f06292;
      transform: scale(1);
    }
    50% {
      color: #f48fb1;
      text-shadow: 0 0 12px #e91e63, 0 0 20px #f06292, 0 0 28px #f8bbd9;
      transform: scale(1.03);
    }
  }

  .word-wealthy {
    animation: wealthyGlow 2.5s ease-in-out infinite;
    display: inline-block;
    font-weight: bold;
  }

  .word-rich {
    animation: richEffect 3s ease-in-out infinite;
    display: inline-block;
    font-weight: bold;
  }

  .word-financially-secure {
    animation: financiallySecurePulse 2.8s ease-in-out infinite;
    display: inline-block;
    font-weight: bold;
  }

  .word-financially {
    font-family: 'Orbitron', sans-serif;
    font-weight: 900;
    color: #ffd700;
    text-shadow: 0 0 8px #ffd700, 0 0 12px #ffed4e;
    display: inline-block;
    animation: financialGold 2.5s ease-in-out infinite;
    letter-spacing: 2px;
  }

  .word-secure {
    font-family: 'Black Ops One', cursive;
    color: #4169e1;
    text-shadow:
      0 0 8px #4169e1,
      0 0 12px #6495ed,
      2px 2px 4px rgba(0, 0, 0, 0.8);
    display: inline-block;
    font-weight: normal;
    animation: secureShield 3s ease-in-out infinite;
    letter-spacing: 2px;
  }

  @keyframes financialGold {
    0%, 100% {
      color: #ffd700;
      text-shadow: 0 0 8px #ffd700, 0 0 12px #ffed4e;
      filter: brightness(1);
    }
    50% {
      color: #ffed4e;
      text-shadow: 0 0 12px #ffd700, 0 0 20px #ffed4e, 0 0 28px #fff9c4;
      filter: brightness(1.2);
    }
  }

  @keyframes secureShield {
    0%, 100% {
      color: #4169e1;
      text-shadow: 0 0 8px #4169e1, 0 0 12px #6495ed;
      transform: scale(1);
    }
    50% {
      color: #6495ed;
      text-shadow: 0 0 12px #4169e1, 0 0 20px #6495ed, 0 0 28px #87ceeb;
      transform: scale(1.01);
    }
  }

  .word-priority {
    animation: priorityEmphasis 2s ease-in-out infinite;
    display: inline-block;
    font-weight: bold;
  }

  @keyframes sunriseGlow {
    0%, 100% {
      color: #ff6b35;
      text-shadow: 0 0 10px #ff6b35, 0 0 20px #ffaa00, 0 0 30px #ffdd00;
    }
    25% {
      color: #ffaa00;
      text-shadow: 0 0 15px #ffaa00, 0 0 25px #ffdd00, 0 0 35px #fff200;
    }
    50% {
      color: #ffdd00;
      text-shadow: 0 0 20px #ffdd00, 0 0 30px #fff200, 0 0 40px #ffffff;
    }
    75% {
      color: #fff200;
      text-shadow: 0 0 15px #fff200, 0 0 25px #ffffff, 0 0 35px #ffaa00;
    }
  }

  .word-sunrise {
    position: relative;
    display: inline-block;
  }

  @keyframes iceGlow {
    0%, 100% {
      color: #00ffff;
      text-shadow: 0 0 10px #00ffff, 0 0 20px #0099ff, 0 0 30px #ffffff;
      filter: brightness(1);
    }
    50% {
      color: #ffffff;
      text-shadow: 0 0 15px #ffffff, 0 0 25px #00ffff, 0 0 35px #0099ff;
      filter: brightness(1.5);
    }
  }

  .word-cool {
    position: relative;
    display: inline-block;
  }


  @keyframes unwrinkleEffect {
    0% {
      color: #888888;
      text-shadow: 1px 1px 0px #666, -1px -1px 0px #666;
      filter: blur(1px) contrast(0.8);
    }
    25% {
      color: #aaaaaa;
      text-shadow: 0.5px 0.5px 0px #777;
      filter: blur(0.5px) contrast(0.9);
    }
    50% {
      color: #cccccc;
      text-shadow: 0px 0px 2px #00ff00;
      filter: blur(0.2px) contrast(1);
    }
    75% {
      color: #00ff00;
      text-shadow: 0 0 5px #00ff00;
      filter: blur(0px) contrast(1.1);
    }
    100% {
      color: #00ff00;
      text-shadow: 0 0 10px #00ff00, 0 0 20px #00ff00;
      filter: blur(0px) contrast(1.2);
    }
  }

  @keyframes proudGlow {
    0%, 100% {
      background-position: 0% 50%;
      filter: brightness(1);
    }
    50% {
      background-position: 100% 50%;
      filter: brightness(1.3);
    }
  }

  .word-proud {
    position: relative;
    display: inline-block;
    background: linear-gradient(45deg, #ffd700, #ffaa00, #ffd700, #ffaa00);
    background-size: 200% 100%;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    font-weight: 900;
    text-transform: uppercase;
  }


  @keyframes stressDistort {
    0% {
      color: #ff0000;
      filter: blur(0.5px) hue-rotate(0deg);
      text-shadow: 1px 0px 0px #ff0000, -1px 0px 0px #ff0000;
    }
    25% {
      color: #ff4444;
      filter: blur(0.2px) hue-rotate(10deg);
      text-shadow: 0px 1px 0px #ff4444, 0px -1px 0px #ff4444;
    }
    50% {
      color: #ff0000;
      filter: blur(0.8px) hue-rotate(-10deg);
      text-shadow: -1px 0px 0px #ff0000, 1px 0px 0px #ff0000;
    }
    75% {
      color: #ff6666;
      filter: blur(0.3px) hue-rotate(5deg);
      text-shadow: 0px -1px 0px #ff6666, 0px 1px 0px #ff6666;
    }
    100% {
      color: #ff0000;
      filter: blur(0.5px) hue-rotate(0deg);
      text-shadow: 1px 0px 0px #ff0000, -1px 0px 0px #ff0000;
    }
  }

  .word-stress {
    display: inline-block;
    font-family: 'Courier New', monospace;
    font-weight: 100;
    text-transform: uppercase;
  }

  @keyframes happinessShimmer {
    0%, 100% {
      background-position: 0% 50%;
      filter: brightness(1);
    }
    25% {
      background-position: 25% 50%;
      filter: brightness(1.1);
    }
    50% {
      background-position: 50% 50%;
      filter: brightness(1.3);
    }
    75% {
      background-position: 75% 50%;
      filter: brightness(1.1);
    }
  }

  .word-happiness {
    position: relative;
    display: inline-block;
    background: linear-gradient(45deg, #ff69b4, #ff1493, #ffb6c1, #ff69b4);
    background-size: 200% 100%;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    font-weight: bold;
  }


  .word-sunrise {
    animation: sunriseGlow 3s ease-in-out infinite;
    display: inline-block;
    font-weight: bold;
  }

  .word-cool {
    animation: iceGlow 2s ease-in-out infinite;
    display: inline-block;
    font-weight: bold;
  }

  .word-unwrinkling {
    animation: unwrinkleEffect 3s ease-in-out infinite;
    display: inline-block;
    font-weight: bold;
  }

  .word-proud {
    animation: proudGlow 2.5s ease-in-out infinite;
    display: inline-block;
  }

  .word-stress {
    animation: stressDistort 0.8s ease-in-out infinite;
    display: inline-block;
  }

  .word-happiness {
    animation: happinessShimmer 2.2s ease-in-out infinite;
    display: inline-block;
    font-weight: bold;
  }

  /* Barcode scanner effect for name recognition */
  @keyframes scannerLine {
    0% {
      top: -100%;
      opacity: 0;
    }
    15% {
      top: -20%;
      opacity: 0.8;
    }
    35% {
      top: 50%;
      opacity: 1;
    }
    50% {
      top: 120%;
      opacity: 0.8;
    }
    65% {
      top: 50%;
      opacity: 1;
    }
    85% {
      top: -20%;
      opacity: 0.8;
    }
    100% {
      top: -100%;
      opacity: 0;
    }
  }

  @keyframes nameScanning {
    0%, 100% {
      text-shadow: 0 0 5px #00ff00;
      filter: brightness(1);
    }
    35%, 65% {
      text-shadow: 0 0 10px #00ff00, 0 0 15px #00ff88, 0 0 20px #00ffaa;
      filter: brightness(1.2);
    }
  }

  .welcome-name-glitch {
    animation: nameScanning 4s ease-in-out;
    display: inline;
    color: #00ff00;
    font-weight: bold;
    position: relative;
    letter-spacing: 1px;
    vertical-align: baseline;
  }

  .welcome-name-glitch::before {
    content: '';
    position: absolute;
    left: -10px;
    right: -10px;
    height: 2px;
    background: linear-gradient(90deg, 
      transparent 0%, 
      #00ff00 20%, 
      #ffffff 50%, 
      #00ff00 80%, 
      transparent 100%
    );
    box-shadow: 0 0 8px #00ff00, 0 0 12px #00ff88;
    animation: scannerLine 4s ease-in-out forwards;
    z-index: 1;
    opacity: 0;
  }

  .welcome-name-glitch::after {
    content: '';
    position: absolute;
    left: -5px;
    right: -5px;
    height: 1px;
    background: linear-gradient(90deg, 
      transparent 0%, 
      rgba(0, 255, 0, 0.3) 30%, 
      rgba(255, 255, 255, 0.6) 50%, 
      rgba(0, 255, 0, 0.3) 70%, 
      transparent 100%
    );
    animation: scannerLine 4s ease-in-out 0.2s forwards;
    z-index: 2;
    opacity: 0;
  }

  /* Loading animation styles */
  .loading-container {
    margin: 0;
    padding: 0;
    font-family: monospace;
  }

  .loading-progress-bar {
    width: 100%;
    max-width: 400px;
    height: 20px;
    border: 1px solid #00ff00;
    background-color: rgba(0, 0, 0, 0.3);
    margin: 10px 0;
    position: relative;
    overflow: hidden;
  }

  .loading-progress-fill {
    height: 100%;
    background: linear-gradient(90deg, #00ff00, #00ff88, #00ffff);
    transition: width 0.8s ease-out;
    position: relative;
  }

  .loading-progress-fill::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
    animation: progressShimmer 2s infinite;
  }

  @keyframes progressShimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }

  .loading-text {
    color: #00ff00;
    text-shadow: 0 0 5px #00ff00;
    animation: pulse 2s infinite;
  }

  .loading-dots {
    color: #00ffff;
    font-weight: bold;
    animation: dotsPulse 1.2s infinite;
  }

  @keyframes dotsPulse {
    0%, 100% { opacity: 0.3; }
    50% { opacity: 1; }
  }

  .sound-waves {
    display: inline-flex;
    flex-direction: column;
    margin-left: 4px;
    font-family: monospace;
  }

  .wave-line {
    color: #00ff00;
    font-size: 10px;
    line-height: 6px;
    opacity: 0.2;
  }

  .wave-line:nth-child(1) {
    animation: soundPulse 1.5s infinite;
  }

  .wave-line:nth-child(2) {
    animation: soundPulse 1.5s infinite 0.5s;
  }

  .wave-line:nth-child(3) {
    animation: soundPulse 1.5s infinite 1s;
  }

  .rsvp-form {
    background-color: rgba(0, 0, 0, 0.8);
    border: 2px solid var(--terminal-highlight);
    padding: 8px;
    width: 100%;
    max-width: 600px;
    margin: 5px auto;
  }

  .rsvp-form h2 {
    color: var(--terminal-highlight);
    margin-bottom: 5px;
    text-align: center;
    font-size: 16px;
  }

  .form-group {
    margin-bottom: 5px;
  }

  .form-group label {
    display: block;
    margin-bottom: 2px;
    color: var(--terminal-highlight);
    font-size: 12px;
  }

  .form-group input, .form-group select {
    width: 100%;
    padding: 6px;
    background-color: #111;
    border: 1px solid var(--terminal-highlight);
    color: #fff;
    font-family: 'Courier New', monospace;
    font-size: 14px;
  }

  .form-group input:focus, .form-group select:focus {
    outline: none;
    border-color: #00ff00;
  }

  .rsvp-buttons {
    margin-top: 5px;
  }

  .rsvp-button {
    padding: 8px 16px;
    background-color: var(--terminal-dark);
    border: 1px solid var(--terminal-highlight);
    color: var(--terminal-highlight);
    cursor: pointer;
    font-family: 'Courier New', monospace;
    transition: all 0.3s;
    font-size: 14px;
    /* Mobile touch improvements */
    min-height: 44px;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
  }

  .rsvp-button:hover, .rsvp-button:active {
    background-color: var(--terminal-highlight);
    color: var(--terminal-dark);
  }

  .rsvp-success {
    text-align: center;
    color: #00ff00;
    margin-top: 10px;
    padding: 8px;
    border: 1px solid #00ff00;
  }

  .video-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    height: calc(100vh - 10px);
    overflow: scroll;
    /* Mobile scroll improvements */
    -webkit-overflow-scrolling: touch;
  }

  .video-reveal {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    height: 100vh;
    padding: 5px;
    overflow: scroll;
    /* Mobile scroll improvements */
    -webkit-overflow-scrolling: touch;
  }
  
  .compact-video {
    width: 100%;
    max-width: 700px;
    margin: 0 auto;
    height: 100%;
  }
  
  .date-display {
    margin-top: 2px;
    margin-bottom: 2px;
  }
  
  .date-label {
    font-size: 14px;
    margin-bottom: 0;
  }
  
  .date-value {
    font-size: 20px;
    margin-top: 0;
  }
  
  .watch-button-container {
    margin: 2px auto;
  }
  
  /* Mobile-specific improvements */
  @media (max-width: 768px) {
    .terminal-option, .rsvp-button {
      min-height: 50px;
      font-size: 16px;
      padding: 12px 20px;
      touch-action: manipulation;
      -webkit-tap-highlight-color: rgba(0, 255, 0, 0.2);
    }
    
    .terminal-text-input {
      font-size: 16px !important; /* Prevent zoom on iOS */
      min-height: 44px;
    }
    
    .video-container, .video-reveal {
      /* Improve mobile scrolling */
      -webkit-overflow-scrolling: touch;
      overflow-y: auto;
    }
  }
  
  /* Touch device improvements */
  @media (hover: none) and (pointer: coarse) {
    .terminal-option, .rsvp-button {
      min-height: 50px;
      padding: 15px 25px;
    }
  }
`;

const TerminalInvite = ({ onComplete, saveRSVP }) => {
  // Simple random glitch function that doesn't cause re-renders
  const getRandomGlitchClass = () => {
    const allGlitchClasses = [
      'system-name-glitch-1',   // Fast glitch
      'system-name-glitch-2',   // Slow glitch  
      'system-name-glitch-3',   // Intense glitch
      'system-name-glitch-4',   // Original glitch
      'system-name-glitch-5',   // Fade glitch (magenta/cyan)
      'system-name-glitch-6',   // Rainbow glitch
      'system-name-glitch-7',   // Blur glitch (pink/blue)
      'system-name-glitch-8',   // Matrix glitch (green/orange/purple)
      'system-name-glitch-9',   // Vaporwave glitch (magenta/cyan/yellow)
      'system-name-glitch-10',  // Corrupted glitch (red/blue/green)
      'system-name-glitch-11'   // Neon glitch (pink/blue/green)
    ];
    
    return allGlitchClasses[Math.floor(Math.random() * allGlitchClasses.length)];
  };

  // Track last used glitch to ensure variety
  const [lastGlitchClass, setLastGlitchClass] = useState('');

  // Helper function to wrap name in glitch span with variety
  const glitchifyName = (name) => {
    let glitchClass;
    let attempts = 0;
    
    // Try to get a different glitch class than the last one used
    do {
      glitchClass = getRandomGlitchClass();
      attempts++;
    } while (glitchClass === lastGlitchClass && attempts < 5);
    
    setLastGlitchClass(glitchClass);
    return `<span class="${glitchClass}">${name.toUpperCase()}</span>`;
  };

  // Helper function to render boot sequence text with proper glitch effects
  const renderBootSequenceText = (text, visitorName) => {
    if (!text || !visitorName) return text;

    // Check if this is the welcome message line and apply glitch effect
    if (text.includes('WELCOME TO YOUR FINANCIAL FUTURE') && text.includes(visitorName.toUpperCase())) {
      const glitchedText = text.replace(
        visitorName.toUpperCase(),
        `<span class="welcome-name-glitch">${visitorName.toUpperCase()}</span>`
      );
      return <span dangerouslySetInnerHTML={{ __html: glitchedText }} />;
    }

    return text;
  };

  // Create dialup-style loading sound with increasing pitch
  const createLoadingSound = (duration) => {
    if (!audioEnabled && isMobile) return null;
    
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const gainNode = audioContext.createGain();
      gainNode.connect(audioContext.destination);
      gainNode.gain.setValueAtTime(0.1, audioContext.currentTime); // Start quiet
      
      let currentFreq = 400; // Start frequency
      const maxFreq = 2200; // End frequency
      const freqStep = (maxFreq - currentFreq) / (duration / 100); // Increase every 100ms
      
      let oscillators = [];
      let noiseBuffer = null;
      
      // Create noise buffer for dialup-like static
      const bufferSize = audioContext.sampleRate * 0.1;
      noiseBuffer = audioContext.createBuffer(1, bufferSize, audioContext.sampleRate);
      const output = noiseBuffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) {
        output[i] = Math.random() * 2 - 1;
      }
      
      const playTone = (freq, startTime, duration) => {
        // Main oscillator
        const oscillator = audioContext.createOscillator();
        const oscGain = audioContext.createGain();
        
        oscillator.type = 'sawtooth';
        oscillator.frequency.setValueAtTime(freq, startTime);
        oscillator.frequency.exponentialRampToValueAtTime(freq * 1.1, startTime + duration);
        
        oscGain.gain.setValueAtTime(0.3, startTime);
        oscGain.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
        
        oscillator.connect(oscGain);
        oscGain.connect(gainNode);
        
        oscillator.start(startTime);
        oscillator.stop(startTime + duration);
        
        // Add some noise for dialup authenticity
        const noiseSource = audioContext.createBufferSource();
        const noiseGain = audioContext.createGain();
        const noiseFilter = audioContext.createBiquadFilter();
        
        noiseSource.buffer = noiseBuffer;
        noiseSource.loop = true;
        noiseFilter.type = 'bandpass';
        noiseFilter.frequency.setValueAtTime(freq * 0.8, startTime);
        noiseFilter.Q.setValueAtTime(10, startTime);
        
        noiseGain.gain.setValueAtTime(0.05, startTime);
        noiseGain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
        
        noiseSource.connect(noiseFilter);
        noiseFilter.connect(noiseGain);
        noiseGain.connect(gainNode);
        
        noiseSource.start(startTime);
        noiseSource.stop(startTime + duration);
        
        return { oscillator, noiseSource };
      };
      
      // Create sequence of tones with increasing pitch
      const startTime = audioContext.currentTime;
      const stepDuration = 0.15; // Each tone lasts 150ms
      const steps = Math.floor(duration / 1000 / stepDuration);
      
      for (let i = 0; i < steps; i++) {
        const freq = currentFreq + (freqStep * i * 10);
        const toneStartTime = startTime + (i * stepDuration);
        const { oscillator, noiseSource } = playTone(freq, toneStartTime, stepDuration * 1.2);
        oscillators.push(oscillator, noiseSource);
      }
      
      // Cleanup function
      return () => {
        try {
          gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.1);
          setTimeout(() => {
            oscillators.forEach(osc => {
              try { osc.stop(); } catch(e) {}
            });
            if (audioContext.state !== 'closed') {
              audioContext.close();
            }
          }, 200);
        } catch(e) {
          console.log('Audio cleanup error:', e);
        }
      };
      
    } catch (error) {
      console.log('Could not create loading sound:', error);
      return null;
    }
  };

  // Progressive loading animation function
  const startLoadingSequence = (callback, totalDuration = 5000) => {
    // DEBUG MODE: Set to true to pause on first loading state for CSS inspection
    const DEBUG_PAUSE = false;

    setLoadingProgress(0);
    setLoadingDots('');
    setLoadingMessage('SYSTEM: LOADING PARTY INVITATION...');

    if (DEBUG_PAUSE) {
      // Show first loading state and PAUSE - don't progress
      setLoadingProgress(40);
      setLoadingMessage('THE TIME HAS COME');
      console.log('🔍 DEBUG MODE: Loading animation PAUSED for CSS inspection');
      console.log('Check the loading bar position now!');
      // Don't call callback - stay paused
      return;
    }

    // Start the loading sound
    const stopLoadingSound = createLoadingSound(totalDuration);

    const steps = [
      { progress: 40, message: 'THE TIME HAS COME', delay: 1800 },
      { progress: 80, message: 'TO SHOW YOU', delay: 1800 },
      { progress: 100, message: 'THE FUTURE', delay: 1400 }
    ];
    
    let currentStep = 0;
    let elapsedTime = 0;
    
    // Dots animation
    const dotsInterval = setInterval(() => {
      setLoadingDots(prev => {
        if (prev.length >= 3) return '';
        return prev + '.';
      });
    }, 400);
    
    // Progress bar animation with proper timing
    const executeStep = (stepIndex) => {
      if (stepIndex < steps.length) {
        const step = steps[stepIndex];
        setLoadingProgress(step.progress);
        setLoadingMessage(step.message);
        
        if (stepIndex < steps.length - 1) {
          // Schedule next step
          setTimeout(() => executeStep(stepIndex + 1), step.delay);
        } else {
          // Final step - complete loading
          setTimeout(() => {
            clearInterval(dotsInterval);
            
            // Stop loading sound
            if (stopLoadingSound) {
              stopLoadingSound();
            }
            
            callback();
          }, step.delay);
        }
      }
    };
    
    // Start the sequence
    executeStep(0);
    
    // Cleanup after total duration as fallback
    setTimeout(() => {
      clearInterval(dotsInterval);
      if (stopLoadingSound) {
        stopLoadingSound();
      }
    }, totalDuration);
  };

  const [stage, setStage] = useState('boot');
  const [currentText, setCurrentText] = useState('');
  const [fullText, setFullText] = useState('');
  const [cursorVisible, setCursorVisible] = useState(true);
  const [question, setQuestion] = useState('');
  const [typedQuestion, setTypedQuestion] = useState('');
  const [questionTyping, setQuestionTyping] = useState(false);
  const [score, setScore] = useState(0);
  const [questionNumber, setQuestionNumber] = useState(0);

  // TV Boot effect states
  const [showTvBoot, setShowTvBoot] = useState(true);
  const [tvBootComplete, setTvBootComplete] = useState(false);
  const [responses, setResponses] = useState([]);
  
  // TV Boot sequence effect
  useEffect(() => {
    if (showTvBoot) {
      // Hide the TV boot overlay after the animation completes
      const bootTimer = setTimeout(() => {
        setShowTvBoot(false);
        setTvBootComplete(true);
      }, 1800); // Matches the TV animation duration
      
      return () => clearTimeout(bootTimer);
    }
  }, [showTvBoot]);
  const [showOptions, setShowOptions] = useState(false);
  const [videoStage, setVideoStage] = useState(0);
  const [romanSize, setRomanSize] = useState(20);
  const [visitorName, setVisitorName] = useState('');
  const [bootComplete, setBootComplete] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingDots, setLoadingDots] = useState('');
  const [loadingMessage, setLoadingMessage] = useState('');

  // Memoized video invitation message to prevent re-renders
  const videoInvitationMessage = useMemo(() => {
    if (visitorName) {
      return (
        <span><span dangerouslySetInnerHTML={{ __html: glitchifyName(visitorName) }} /> YOU ARE INVITED TO OUR BIRTHDAY PARTY.</span>
      );
    } else {
      return "YOU ARE INVITED TO OUR BIRTHDAY PARTY.";
    }
  }, [visitorName]);
  const [userInput, setUserInput] = useState('');
  const [waitingForInput, setWaitingForInput] = useState(false);
  const terminalRef = useRef(null);
  const inputRef = useRef(null);
  const rsvpSuccessRef = useRef(null);

  // Mobile-specific state
  const [isMobile, setIsMobile] = useState(false);
  const [pageVisible, setPageVisible] = useState(true);
  const [audioEnabled, setAudioEnabled] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [lastStage, setLastStage] = useState('boot');
  const [lowBattery, setLowBattery] = useState(false);
  
  // Update audio refs to include cha-ching sound with better mobile handling
  const startupSoundRef = useRef(null);
  const revealSoundRef = useRef(null);
  const chachingSoundRef = useRef(null);
  const ambientMusicRef = useRef(null);
  
  // Audio context for terminal sound effects
  const audioContextRef = useRef(null);
  
  // Initialize audio refs with better error handling
  useEffect(() => {
    try {
      startupSoundRef.current = new Audio('/startup_sound.mp3');
      revealSoundRef.current = new Audio('/three_reveal_sound.mp3');
      chachingSoundRef.current = new Audio('/chaching.mp3');
      
      // Set audio properties for better mobile compatibility
      [startupSoundRef, revealSoundRef, chachingSoundRef].forEach(audioRef => {
        if (audioRef.current) {
          audioRef.current.preload = 'auto';
          audioRef.current.crossOrigin = 'anonymous';
          // Reduce volume for mobile
          audioRef.current.volume = 0.7;
        }
      });
      
      // Initialize Web Audio API for terminal sounds
      if (typeof window !== 'undefined' && (window.AudioContext || window.webkitAudioContext)) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
    } catch (error) {
      console.log('Audio initialization failed:', error);
    }
  }, []);
  
  // Party date and video link
  const partyDate = "NOVEMBER 2025";
  // Updated Vimeo embed URL with the correct format
  const vimeoEmbedUrl = "https://player.vimeo.com/video/1065367766?h=d3a6894c79&badge=0&autopause=0&player_id=0&app_id=58479";
  
  // Add new state for initial interaction
  const [hasInteracted, setHasInteracted] = useState(false);
  
  // Visual effects state
  const [showMatrixRain, setShowMatrixRain] = useState(false);
  const [showDigitalRain, setShowDigitalRain] = useState(false);
  const [showParticles, setShowParticles] = useState(false);
  const [particles, setParticles] = useState([]);
  const [backgroundMatrix, setBackgroundMatrix] = useState(false); // Subtle background matrix
  
  // Add new state variables for RSVP form
  const [showRSVPForm, setShowRSVPForm] = useState(false);
  const [rsvpData, setRsvpData] = useState({
    attending: 'yes',
    name: '',
    guests: '0'
  });
  const [rsvpSubmitted, setRsvpSubmitted] = useState(false);
  
  // Add a new state for tracking if user has confirmed watching the video
  const [videoWatched, setVideoWatched] = useState(false);
  
  // Add comprehensive video loading state management
  const [videoLoadingStates, setVideoLoadingStates] = useState({
    hype1: 'loading' // 'loading', 'loaded', 'error', 'timeout'
  });
  const [videoRetryCount, setVideoRetryCount] = useState({
    hype1: 0
  });
  const [showVideoError, setShowVideoError] = useState(false);
  const [videoLoadTimeout, setVideoLoadTimeout] = useState(null);
  
  // Define video URLs with quality options
  const videoUrls = {
    hype1: {
      hd: "https://player.vimeo.com/video/1105351638?h=91399453d5&badge=0&autopause=0&player_id=0&app_id=58479",
      sd: "https://player.vimeo.com/video/1105351638?h=91399453d5&badge=0&autopause=0&player_id=0&app_id=58479&quality=540p"
    }
  };
  
  // Track video quality preference
  const [videoQuality, setVideoQuality] = useState(isMobile ? 'sd' : 'hd');
  
  // Video loading timeout management
  useEffect(() => {
    if (stage === 'video' && videoStage === 2) {
      // Set up timeout for video loading
      const timeout = setTimeout(() => {
        // Check if any videos are still loading after 15 seconds
        setVideoLoadingStates(prev => {
          const updated = { ...prev };
          Object.keys(updated).forEach(key => {
            if (updated[key] === 'loading') {
              updated[key] = 'timeout';
            }
          });
          return updated;
        });
        setShowVideoError(true);
      }, 15000); // 15 second timeout
      
      setVideoLoadTimeout(timeout);
      
      return () => {
        if (timeout) clearTimeout(timeout);
      };
    }
  }, [stage, videoStage]);
  
  // Handle video loading events
  const handleVideoLoad = (videoKey) => {
    console.log(`Video ${videoKey} loaded successfully`);
    setVideoLoadingStates(prev => ({
      ...prev,
      [videoKey]: 'loaded'
    }));
    
    // Clear error state since we only have one video now
    setShowVideoError(false);
  };
  
  const handleVideoError = (videoKey) => {
    console.log(`Video ${videoKey} failed to load`);
    setVideoLoadingStates(prev => ({
      ...prev,
      [videoKey]: 'error'
    }));
    
    // Show error since we only have one video now
    setShowVideoError(true);
  };
  
  // Retry video loading
  const retryVideo = (videoKey) => {
    const currentRetries = videoRetryCount[videoKey] || 0;
    if (currentRetries < 3) {
      setVideoRetryCount(prev => ({
        ...prev,
        [videoKey]: currentRetries + 1
      }));
      
      setVideoLoadingStates(prev => ({
        ...prev,
        [videoKey]: 'loading'
      }));
      
      setShowVideoError(false);
      
      // Force iframe reload by changing src slightly
      setTimeout(() => {
        const iframe = document.querySelector(`iframe[data-video="${videoKey}"]`);
        if (iframe) {
          const currentSrc = iframe.src;
          iframe.src = '';
          setTimeout(() => {
            iframe.src = currentSrc + '&retry=' + Date.now();
          }, 100);
        }
      }, 500);
    }
  };
  
  // Switch to lower quality on repeated failures
  const switchToLowerQuality = () => {
    if (videoQuality === 'hd') {
      setVideoQuality('sd');
      setVideoLoadingStates({
        hype1: 'loading'
      });
      setShowVideoError(false);
      
      // Reset retry counts
      setVideoRetryCount({
        hype1: 0
      });
    }
  };
  
  // Get current video URL based on quality
  const getCurrentVideoUrl = (videoKey) => {
    const baseUrl = videoUrls[videoKey][videoQuality];
    const retries = videoRetryCount[videoKey] || 0;
    return retries > 0 ? `${baseUrl}&retry=${retries}` : baseUrl;
  };
  
  // Preload videos when component mounts
  useEffect(() => {
    if (hasInteracted) {
      // Start preloading videos in background
      Object.keys(videoUrls).forEach(videoKey => {
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.src = getCurrentVideoUrl(videoKey);
        iframe.onload = () => handleVideoLoad(videoKey);
        iframe.onerror = () => handleVideoError(videoKey);
        document.body.appendChild(iframe);
        
        // Clean up after 30 seconds
        setTimeout(() => {
          try {
            document.body.removeChild(iframe);
          } catch (e) {
            // Iframe may have already been removed
          }
        }, 30000);
      });
    }
  }, [hasInteracted, videoQuality]);
  
  // Loading indicator component
  const VideoLoadingIndicator = ({ videoKey }) => {
    const loadingState = videoLoadingStates[videoKey];
    const retries = videoRetryCount[videoKey] || 0;
    
    if (loadingState === 'loaded') return null;
    
    return (
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 10,
        border: '2px solid var(--terminal-highlight)'
      }}>
        {loadingState === 'loading' && (
          <>
            <div style={{
              color: 'var(--terminal-highlight)',
              fontSize: isMobile ? '14px' : '16px',
              marginBottom: '10px',
              textAlign: 'center'
            }}>
              LOADING VIDEO...
            </div>
            <div style={{
              width: '50px',
              height: '50px',
              border: '3px solid transparent',
              borderTop: '3px solid var(--terminal-highlight)',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            {retries > 0 && (
              <div style={{
                color: 'var(--terminal-highlight)',
                fontSize: '12px',
                marginTop: '10px',
                opacity: 0.7
              }}>
                RETRY {retries}/3
              </div>
            )}
          </>
        )}
        
        {(loadingState === 'error' || loadingState === 'timeout') && (
          <>
            <div style={{
              color: 'var(--terminal-negative)',
              fontSize: isMobile ? '14px' : '16px',
              marginBottom: '15px',
              textAlign: 'center'
            }}>
              {loadingState === 'timeout' ? 'VIDEO LOAD TIMEOUT' : 'VIDEO LOAD ERROR'}
            </div>
            
            {retries < 3 && (
              <button
                onClick={() => retryVideo(videoKey)}
                style={{
                  backgroundColor: 'var(--terminal-header)',
                  border: '1px solid var(--terminal-highlight)',
                  color: 'var(--terminal-highlight)',
                  padding: isMobile ? '12px 20px' : '8px 16px',
                  fontSize: isMobile ? '14px' : '12px',
                  cursor: 'pointer',
                  marginBottom: '10px',
                  fontFamily: 'var(--terminal-font)',
                  touchAction: 'manipulation'
                }}
              >
                RETRY VIDEO
              </button>
            )}
            
            {videoQuality === 'hd' && (
              <button
                onClick={switchToLowerQuality}
                style={{
                  backgroundColor: 'var(--terminal-header)',
                  border: '1px solid var(--terminal-highlight)',
                  color: 'var(--terminal-highlight)',
                  padding: isMobile ? '10px 16px' : '6px 12px',
                  fontSize: isMobile ? '12px' : '10px',
                  cursor: 'pointer',
                  marginBottom: '10px',
                  fontFamily: 'var(--terminal-font)',
                  touchAction: 'manipulation'
                }}
              >
                SWITCH TO SD QUALITY
              </button>
            )}
            
            <div style={{
              color: 'var(--terminal-text)',
              fontSize: '10px',
              textAlign: 'center',
              opacity: 0.7,
              maxWidth: '200px'
            }}>
              Check your connection or try refreshing the page
            </div>
          </>
        )}
      </div>
    );
  };
  

  
  // Scroll to RSVP success message on mobile
  useEffect(() => {
    if (rsvpSubmitted && isMobile && rsvpSuccessRef.current) {
      setTimeout(() => {
        rsvpSuccessRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
    }
  }, [rsvpSubmitted, isMobile]);

  // Manage loading indicators for past party videos
  useEffect(() => {
    if (rsvpSubmitted) {
      // Show loading indicators for past party videos initially
      const loadingOverlays = document.querySelectorAll('.past-video-loading');
      loadingOverlays.forEach(overlay => {
        overlay.style.opacity = '1';
        overlay.style.pointerEvents = 'auto';
      });

      // Hide loading indicators after a delay (simulating load time)
      const timeouts = Array.from(loadingOverlays).map((overlay, index) => {
        return setTimeout(() => {
          overlay.style.opacity = '0';
          overlay.style.pointerEvents = 'none';
        }, (index + 1) * 2000 + Math.random() * 2000); // Staggered loading with some randomness
      });

      return () => {
        timeouts.forEach(timeout => clearTimeout(timeout));
      };
    }
  }, [rsvpSubmitted]);
  
  // Handle iframe loading events for better UX
  useEffect(() => {
    const handleIframeLoad = (event) => {
      const iframe = event.target;
      if (iframe.tagName === 'IFRAME') {
        // Find the associated loading overlay
        const container = iframe.closest('.past-party-video');
        if (container) {
          const loadingOverlay = container.querySelector('.past-video-loading');
          if (loadingOverlay) {
            loadingOverlay.style.opacity = '0';
            loadingOverlay.style.pointerEvents = 'none';
          }
        }
      }
    };
    
    const handleIframeError = (event) => {
      const iframe = event.target;
      if (iframe.tagName === 'IFRAME') {
        // Find the associated loading overlay and show error
        const container = iframe.closest('.past-party-video');
        if (container) {
          const loadingOverlay = container.querySelector('.past-video-loading');
          if (loadingOverlay) {
            const loadingText = loadingOverlay.querySelector('div:first-child');
            if (loadingText) {
              loadingText.textContent = 'ERROR LOADING';
            }
            // Hide after showing error briefly
            setTimeout(() => {
              loadingOverlay.style.opacity = '0';
              loadingOverlay.style.pointerEvents = 'none';
            }, 3000);
          }
        }
      }
    };
    
    // Add event listeners for all iframes
    document.addEventListener('load', handleIframeLoad, true);
    document.addEventListener('error', handleIframeError, true);
    
    return () => {
      document.removeEventListener('load', handleIframeLoad, true);
      document.removeEventListener('error', handleIframeError, true);
    };
  }, []);
  
  // Clean up video loading timeout on unmount
  useEffect(() => {
    return () => {
      if (videoLoadTimeout) {
        clearTimeout(videoLoadTimeout);
      }
    };
  }, [videoLoadTimeout]);
  
  // Detect mobile device, low battery mode, and setup mobile-specific handlers
  useEffect(() => {
    const checkMobile = () => {
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
                           ('ontouchstart' in window) ||
                           (navigator.maxTouchPoints > 0);
      setIsMobile(isMobileDevice);
      
      // Check for low battery mode (when available)
      if ('getBattery' in navigator) {
        navigator.getBattery().then(battery => {
          const isLowBattery = battery.level < 0.2 || !battery.charging;
          setLowBattery(isLowBattery);
          
          // Listen for battery changes
          battery.addEventListener('levelchange', () => {
            setLowBattery(battery.level < 0.2 || !battery.charging);
          });
          battery.addEventListener('chargingchange', () => {
            setLowBattery(battery.level < 0.2 || !battery.charging);
          });
        }).catch(() => {
          // Battery API not supported, that's ok
        });
      }
      
      // Set viewport for mobile if not already set
      if (isMobileDevice) {
        let viewport = document.querySelector('meta[name="viewport"]');
        if (!viewport) {
          viewport = document.createElement('meta');
          viewport.name = 'viewport';
          viewport.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no';
          document.head.appendChild(viewport);
        }
      }
    };
    
    checkMobile();
    
    // Handle page visibility changes (important for mobile)
    const handleVisibilityChange = () => {
      setPageVisible(!document.hidden);
    };
    
    // Handle orientation change on mobile
    const handleOrientationChange = () => {
      if (isMobile) {
        // Small delay to allow orientation to complete
        setTimeout(() => {
          if (terminalRef.current) {
            terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
          }
        }, 300);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('orientationchange', handleOrientationChange);
    window.addEventListener('resize', handleOrientationChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('orientationchange', handleOrientationChange);
      window.removeEventListener('resize', handleOrientationChange);
    };
  }, [isMobile]);
  
  // Load Vimeo player script once when component mounts
  useEffect(() => {
    // Check if script already exists to avoid duplicates
    if (!document.querySelector('script[src="https://player.vimeo.com/api/player.js"]')) {
      const script = document.createElement('script');
      script.src = "https://player.vimeo.com/api/player.js";
      script.async = true;
      document.body.appendChild(script);
      
      return () => {
        // Clean up script when component unmounts
        try {
          document.body.removeChild(script);
        } catch (e) {
          // Script may have already been removed
        }
      };
    }
  }, []);

  // Check URL for name parameters
  useEffect(() => {
    // Get the current URL
    const url = window.location.href;
    
    // Remove any existing query params and get just the search part
    const searchParams = url.split('?')[1];
    
    if (searchParams) {
      // Check if there's a name parameter (without a specific key)
      const possibleNames = searchParams.split('&');
      
      // Use the first parameter as a name if it doesn't have an equals sign
      const name = possibleNames.find(param => !param.includes('='));

      if (name) {
        // Decode URL encoding and replace underscores with spaces
        const decodedName = decodeURIComponent(name).replace(/_/g, ' ');
        // Capitalize first letter of each word for proper name formatting
        const capitalizedName = decodedName
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ');
        setVisitorName(capitalizedName);
      }
    }
  }, []);
  
  // Cursor blink effect with mobile-friendly timing
  useEffect(() => {
    const interval = setInterval(() => {
      setCursorVisible(prev => !prev);
    }, isMobile ? 600 : 530); // Slightly slower on mobile
    return () => clearInterval(interval);
  }, [isMobile]);
  
  // Robust boot sequence with mobile compatibility and retry logic
  useEffect(() => {
    if (stage === 'boot' && hasInteracted && pageVisible) {
      // Store stage for recovery
      setLastStage('boot');
      
      // Try to play startup sound with better mobile handling
      const playStartupSound = async () => {
        if (!audioEnabled && isMobile) {
          // Skip audio on mobile if not enabled
          return;
        }
        
        // Initial delay before first attempt
        await new Promise(resolve => setTimeout(resolve, 800));
        
        try {
          if (startupSoundRef.current) {
            await startupSoundRef.current.play();
            setAudioEnabled(true);
          }
        } catch (err) {
          console.log('Audio playback failed:', err);
          // Don't retry audio on mobile to avoid delays
          if (!isMobile) {
            setTimeout(async () => {
              try {
                if (startupSoundRef.current) {
                  await startupSoundRef.current.play();
                  setAudioEnabled(true);
                }
              } catch (retryErr) {
                console.log('Retry audio playback failed:', retryErr);
              }
            }, 1000);
          }
        }
      };

      playStartupSound();

      // Customize the welcome message if a name is provided - keep simple for typing
      const welcomeMessage = visitorName
        ? `WELCOME TO YOUR FINANCIAL FUTURE, ${visitorName.toUpperCase()}`
        : "WELCOME TO YOUR FINANCIAL FUTURE.";

      const bootSequence = [
        "CONNECTION INITIATED...",
        "PREPARING RISKS AND REWARDS...",
        welcomeMessage
      ];

      let currentIndex = 0;
      let charIndex = 0;
      let currentLine = '';
      let fullTextContent = '';

      // Adjust typing speed for mobile (faster to prevent timeouts)
      const typingSpeed = isMobile ? 20 : 30;

      const typeInterval = setInterval(() => {
        // Check if page is still visible (important for mobile)
        if (!pageVisible) {
          clearInterval(typeInterval);
          // Set a flag to retry when page becomes visible again
          setRetryCount(prev => prev + 1);
          return;
        }

        if (currentIndex < bootSequence.length) {
          if (charIndex < bootSequence[currentIndex].length) {
            currentLine += bootSequence[currentIndex][charIndex];
            setCurrentText(currentLine);
            charIndex++;
          } else {
            // Add current line to full text
            fullTextContent += currentLine + '\n\n';
            setFullText(fullTextContent);

            // Move to next line
            currentLine = '';
            charIndex = 0;
            currentIndex++;

            // Clear current text when moving to a new line
            setCurrentText('');

            // Scroll to bottom with mobile-friendly timing
            setTimeout(() => {
              if (terminalRef.current) {
                terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
              }
            }, 50);
          }
        } else {
          clearInterval(typeInterval);
          // Mark boot as complete but don't change stage yet
          setBootComplete(true);

          // Clear current text at the end to avoid duplication with fullText
          setCurrentText('');

          // Show animated loading message with ASCII loader
          const loadingFrames = [
            '[▱▱▱▱▱▱▱▱▱▱] LOADING QUESTIONNAIRE...',
            '[▰▱▱▱▱▱▱▱▱▱] LOADING QUESTIONNAIRE...',
            '[▰▰▱▱▱▱▱▱▱▱] LOADING QUESTIONNAIRE...',
            '[▰▰▰▱▱▱▱▱▱▱] LOADING QUESTIONNAIRE...',
            '[▰▰▰▰▱▱▱▱▱▱] LOADING QUESTIONNAIRE...',
            '[▰▰▰▰▰▱▱▱▱▱] LOADING QUESTIONNAIRE...',
            '[▰▰▰▰▰▰▱▱▱▱] LOADING QUESTIONNAIRE...',
            '[▰▰▰▰▰▰▰▱▱▱] LOADING QUESTIONNAIRE...',
            '[▰▰▰▰▰▰▰▰▱▱] LOADING QUESTIONNAIRE...',
            '[▰▰▰▰▰▰▰▰▰▱] LOADING QUESTIONNAIRE...',
            '[▰▰▰▰▰▰▰▰▰▰] LOADING QUESTIONNAIRE...',
            '[▰▰▰▰▰▰▰▰▰▰] QUESTIONNAIRE READY!'
          ];

          let frameIndex = 0;
          const loadingInterval = setInterval(() => {
            if (frameIndex < loadingFrames.length) {
              setCurrentText(loadingFrames[frameIndex]);
              frameIndex++;
            } else {
              clearInterval(loadingInterval);
            }
          }, 200);

          // Move to next stage with longer timing for loading animation
          setTimeout(() => {
            clearInterval(loadingInterval);
            setCurrentText(''); // Clear loading message
            setFullText(''); // Clear boot sequence text so questionnaire is clean
            setStage('rich');
            setQuestionNumber(1);
            setQuestion(visitorName ? `${visitorName.toUpperCase()}, DO YOU WANT TO BE RICH?` : "DO YOU WANT TO BE RICH?");
            setResponses([]);
            setShowOptions(true);
            setWaitingForInput(true);
            setLastStage('rich');

            // Focus on input when ready for user input (with mobile check)
            setTimeout(() => {
              if (inputRef.current && !isMobile) {
                try {
                  inputRef.current.focus();
                } catch (e) {
                  // Focus may fail, that's ok
                }
              }
            }, 100);
          }, 2600); // 12 frames * 200ms + 200ms buffer
        }
      }, typingSpeed);

      return () => clearInterval(typeInterval);
    }
  }, [stage, visitorName, hasInteracted, pageVisible, isMobile, audioEnabled]);
  
  // Retry mechanism for when page becomes visible again
  useEffect(() => {
    if (pageVisible && retryCount > 0 && stage === 'boot' && !bootComplete) {
      // Reset and retry boot sequence
      setCurrentText('');
      setFullText('');
      setRetryCount(0);
      // The boot sequence effect will restart automatically
    }
  }, [pageVisible, retryCount, stage, bootComplete]);

  // Type out questions with cursor effect
  useEffect(() => {
    if (!question || stage === 'boot' || stage === 'video') return;

    setQuestionTyping(true);
    setTypedQuestion('');
    setShowOptions(false);

    let charIndex = 0;
    const typingSpeed = isMobile ? 15 : 20;

    const typeInterval = setInterval(() => {
      if (charIndex < question.length) {
        setTypedQuestion(question.substring(0, charIndex + 1));
        charIndex++;
      } else {
        clearInterval(typeInterval);
        setQuestionTyping(false);
        setShowOptions(true);
      }
    }, typingSpeed);

    return () => clearInterval(typeInterval);
  }, [question, stage, isMobile]);

  // Handle terminal responses for each stage with mobile improvements
  useEffect(() => {
    if (stage === 'rich' || stage === 'wealthy' || stage === 'health' || stage === 'unknown') {
      setLastStage(stage);

      // Don't auto-scroll if we're showing the loading animation - let it appear naturally
      const isShowingLoading = responses.length > 0 && responses[responses.length - 1] === 'LOADING_ANIMATION';

      if (terminalRef.current && !isShowingLoading) {
        // Use smooth scrolling on mobile
        if (isMobile) {
          terminalRef.current.scrollTo({
            top: terminalRef.current.scrollHeight,
            behavior: 'smooth'
          });
        } else {
          terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
        }
      }

      // Focus on input when ready for user input (desktop only)
      if (waitingForInput && inputRef.current && !isMobile) {
        setTimeout(() => {
          try {
            inputRef.current.focus();
          } catch (e) {
            // Focus may fail, that's ok
          }
        }, 200);
      }
    }
  }, [stage, responses, waitingForInput, isMobile]);
  
  // Terminal sound effects using Web Audio API
  const playTerminalSound = (type = 'yes') => {
    if (!audioContextRef.current || (isMobile && !audioEnabled)) return;
    
    try {
      const audioContext = audioContextRef.current;
      
      // Resume audio context if suspended (required for some browsers)
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }
      
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Different frequencies and patterns for yes/no
      if (type === 'yes') {
        // Higher pitch, ascending beep for "yes"
        oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(1000, audioContext.currentTime + 0.1);
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.15);
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.15);
      } else if (type === 'no') {
        // Lower pitch, descending beep for "no"
        oscillator.frequency.setValueAtTime(600, audioContext.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(400, audioContext.currentTime + 0.1);
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.2);
      } else if (type === 'error') {
        // Error sound - double low beep
        oscillator.frequency.setValueAtTime(300, audioContext.currentTime);
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.1);
        
        // Second beep
        const oscillator2 = audioContext.createOscillator();
        const gainNode2 = audioContext.createGain();
        oscillator2.connect(gainNode2);
        gainNode2.connect(audioContext.destination);
        oscillator2.type = 'square';
        oscillator2.frequency.setValueAtTime(300, audioContext.currentTime + 0.15);
        gainNode2.gain.setValueAtTime(0.3, audioContext.currentTime + 0.15);
        gainNode2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.25);
        oscillator2.start(audioContext.currentTime + 0.15);
        oscillator2.stop(audioContext.currentTime + 0.25);
      } else {
        // Generic click sound
        oscillator.frequency.setValueAtTime(700, audioContext.currentTime);
        gainNode.gain.setValueAtTime(0.2, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.1);
      }
      
      oscillator.type = 'square'; // Retro square wave
    } catch (error) {
      console.log('Terminal sound failed:', error);
    }
  };

  // Matrix rain effect
  useEffect(() => {
    if (!showMatrixRain) return;
    
    const stockMarketChars = ['0', '1', '$']; // Stock market themed characters
    const matrixContainer = document.querySelector('.matrix-rain');
    
    if (!matrixContainer) return;
    
    const createMatrixColumn = () => {
      const column = document.createElement('div');
      column.className = 'matrix-column';
      
      // Generate shorter blocks of 2-4 characters instead of long lines
      const blockLength = Math.floor(Math.random() * 3) + 2; // 2-4 characters
      let chars = '';
      for (let i = 0; i < blockLength; i++) {
        chars += stockMarketChars[Math.floor(Math.random() * stockMarketChars.length)];
        if (i < blockLength - 1) chars += '\n';
      }
      
      column.setAttribute('data-chars', chars);
      column.style.left = Math.random() * 100 + '%';
      column.style.animationDuration = (Math.random() * 4 + 3) + 's'; // Slower fall
      column.style.animationDelay = Math.random() * 3 + 's'; // More spacing
      
      matrixContainer.appendChild(column);
      
      // Remove after animation
      setTimeout(() => {
        if (matrixContainer.contains(column)) {
          matrixContainer.removeChild(column);
        }
      }, 7000); // Longer cleanup time for slower animation
    };
    
    // Create columns less frequently for subtler effect
    const interval = setInterval(createMatrixColumn, 1500); // Less frequent
    
    // Initial columns - fewer of them
    for (let i = 0; i < 5; i++) {
      setTimeout(createMatrixColumn, i * 500);
    }
    
    return () => {
      clearInterval(interval);
      if (matrixContainer) {
        matrixContainer.innerHTML = '';
      }
    };
  }, [showMatrixRain]);
  
  // Background matrix rain effect - more subtle
  useEffect(() => {
    console.log('Background matrix effect triggered, backgroundMatrix:', backgroundMatrix); // Debug log
    if (!backgroundMatrix) return;
    
    const stockMarketChars = ['0', '1', '$']; // Stock market themed characters
    const matrixContainer = document.querySelector('.matrix-rain');
    
    console.log('Matrix container found:', !!matrixContainer); // Debug log
    if (!matrixContainer) return;
    
    const createBackgroundColumn = () => {
      console.log('Creating background matrix column'); // Debug log
      const column = document.createElement('div');
      column.className = 'matrix-column background-matrix';
      
      // More characters for denser background - 2-5 characters
      const blockLength = Math.floor(Math.random() * 4) + 2; // 2-5 characters
      let chars = '';
      for (let i = 0; i < blockLength; i++) {
        const char = stockMarketChars[Math.floor(Math.random() * stockMarketChars.length)];
        chars += char;
        if (i < blockLength - 1) chars += '\n';
      }
      
      column.setAttribute('data-chars', chars);
      
      // Add a slight green tint to $ signs for stock market feel
      if (chars.includes('$')) {
        column.style.color = '#00ff40'; // Slightly different green for $ signs
      }
      column.style.left = Math.random() * 100 + '%';
      column.style.animationDuration = (Math.random() * 4 + 3) + 's'; // Faster fall for more activity
      column.style.animationDelay = Math.random() * 2 + 's'; // Less spacing
      
      matrixContainer.appendChild(column);
      
      // Remove after animation
      setTimeout(() => {
        if (matrixContainer.contains(column)) {
          matrixContainer.removeChild(column);
        }
      }, 8000); // Shorter cleanup time
    };
    
    // Create columns more frequently for denser effect during video
    const interval = setInterval(createBackgroundColumn, 800); // Every 0.8 seconds - much more frequent
    
    // Initial burst of columns for immediate effect
    for (let i = 0; i < 8; i++) {
      setTimeout(createBackgroundColumn, Math.random() * 1000);
    }
    
    return () => {
      clearInterval(interval);
      // Clean up background matrix columns
      const backgroundColumns = document.querySelectorAll('.background-matrix');
      backgroundColumns.forEach(column => {
        if (column.parentNode) {
          column.parentNode.removeChild(column);
        }
      });
    };
  }, [backgroundMatrix]);
  
  // Digital rain effect for loading
  useEffect(() => {
    if (!showDigitalRain) return;
    
    const rainContainer = document.querySelector('.digital-rain');
    if (!rainContainer) return;
    
    const binaryChars = ['0', '1'];
    
    const createRainDrop = () => {
      const drop = document.createElement('div');
      drop.className = 'rain-drop';
      drop.textContent = binaryChars[Math.floor(Math.random() * binaryChars.length)];
      drop.style.left = Math.random() * 100 + '%';
      drop.style.animationDuration = (Math.random() * 2 + 1) + 's';
      
      rainContainer.appendChild(drop);
      
      setTimeout(() => {
        if (rainContainer.contains(drop)) {
          rainContainer.removeChild(drop);
        }
      }, 3000);
    };
    
    const interval = setInterval(createRainDrop, 100);
    
    return () => {
      clearInterval(interval);
      if (rainContainer) {
        rainContainer.innerHTML = '';
      }
    };
  }, [showDigitalRain]);
  
  // Particle explosion effect
  const createParticleExplosion = (centerX, centerY) => {
    const newParticles = [];
    
    for (let i = 0; i < 15; i++) {
      const angle = (Math.PI * 2 * i) / 15;
      const velocity = 50 + Math.random() * 100;
      const dx = Math.cos(angle) * velocity;
      const dy = Math.sin(angle) * velocity;
      
      newParticles.push({
        id: Date.now() + i,
        x: centerX,
        y: centerY,
        dx,
        dy
      });
    }
    
    setParticles(newParticles);
    
    // Clear particles after animation
    setTimeout(() => {
      setParticles([]);
    }, 2000);
  };
  
  // Screen shake effect
  const triggerScreenShake = () => {
    const container = document.querySelector('.terminal-container');
    if (container) {
      container.classList.add('screen-shake');
      setTimeout(() => {
        container.classList.remove('screen-shake');
      }, 500);
    }
  };
  
  // Glitch effect for text with color variants
  // Subtle glitch effect for text with color variants
  const triggerGlitchEffect = (element, variant = 'default') => {
    if (element) {
      // Check for low battery mode or reduced motion preferences
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const shouldMinimize = prefersReducedMotion || lowBattery;
      
      const text = element.textContent;
      element.setAttribute('data-text', text);
      
      // Remove any existing glitch classes
      element.classList.remove('glitch-effect', 'glitch-green', 'glitch-red');
      
      // For reduced motion or low battery, just use subtle color changes
      if (shouldMinimize) {
        if (variant === 'green') {
          element.style.borderColor = 'rgba(0, 255, 0, 0.3)';
          element.style.backgroundColor = 'rgba(0, 255, 0, 0.02)';
        } else if (variant === 'red') {
          element.style.borderColor = 'rgba(255, 0, 0, 0.3)';
          element.style.backgroundColor = 'rgba(255, 0, 0, 0.02)';
        }
        // Reset after a brief moment
        setTimeout(() => {
          element.style.borderColor = '';
          element.style.backgroundColor = '';
        }, 80);
        return;
      }
      
      // Add appropriate glitch class based on variant
      if (variant === 'green') {
        element.classList.add('glitch-green');
      } else if (variant === 'red') {
        element.classList.add('glitch-red');
      } else {
        element.classList.add('glitch-effect');
      }
      
      // Very short duration for subtlety, even shorter on low battery
      const duration = lowBattery ? 100 : (isMobile ? 120 : 150);
      setTimeout(() => {
        element.classList.remove('glitch-effect', 'glitch-green', 'glitch-red');
      }, duration);
    }
  };

  // Helper function to render text with bold formatting
  const renderFormattedText = (text) => {
    // Handle loading animation display
    if (text === 'LOADING_ANIMATION') {
      return (
        <div className="loading-container" style={{ textAlign: 'center', margin: '0', padding: '0' }}>
          <div className="loading-text" style={{ margin: '0', padding: '0' }}>
            {loadingMessage}<span className="loading-dots">{loadingDots}</span>
          </div>
          <div className="loading-progress-bar" style={{ margin: '5px auto 0', maxWidth: '400px' }}>
            <div
              className="loading-progress-fill"
              style={{ width: `${loadingProgress}%` }}
            />
          </div>
        </div>
      );
    }
    
    // First handle HTML spans (for glitch effects), then word highlighting, then bold formatting
    if (text.includes('<span class="system-name-glitch-') || text.includes('<span class="welcome-name-glitch">')) {
      return <span dangerouslySetInnerHTML={{ __html: text }} />;
    }
    
    // Apply word highlighting for thematic effects
    let processedText = text;

    // Replace key words with styled spans
    processedText = processedText.replace(/\bWEALTHY\b/g, '<span class="word-wealthy">WEALTHY</span>');
    processedText = processedText.replace(/\bRICH\b/g, '<span class="word-rich">RICH</span>');
    processedText = processedText.replace(/\bOVERLY FINANCIALLY SECURE\b/g, 'OVERLY <span class="word-financially">FINANCIALLY</span> <span class="word-secure">SECURE</span>');
    processedText = processedText.replace(/\bPRIORITY\b/g, '<span class="word-priority">PRIORITY</span>');

    // Special effects for the long question - with thematic fonts and animations
    processedText = processedText.replace(/\bSUNRISE\b/g, '<span class="word-sunrise">SUNRISE</span>');
    processedText = processedText.replace(/\bCOOL\b/g, '<span class="word-cool">COOL</span>');
    processedText = processedText.replace(/\bUNWRINKLING\b/g, '<span class="word-unwrinkling">UNWRINKLING</span>');
    processedText = processedText.replace(/\bPROUD\b/g, '<span class="word-proud">PROUD</span>');
    processedText = processedText.replace(/\bSTRESS\b/g, '<span class="word-stress">STRESS</span>');
    processedText = processedText.replace(/\bHAPPINESS\b/g, '<span class="word-happiness">HAPPINESS</span>');
    processedText = processedText.replace(/\bWORRY\b/g, '<span class="word-worry">WORRY</span>');
    processedText = processedText.replace(/\bCRIPPLING\b/g, '<span class="word-crippling">CRIPPLING</span>');
    processedText = processedText.replace(/\bUNCERTAINTY\b/g, '<span class="word-uncertainty">UNCERTAINTY</span>');
    processedText = processedText.replace(/\bYOUTHFUL\b/g, '<span class="word-youthful">YOUTHFUL</span>');
    processedText = processedText.replace(/\bCAREFREE\b/g, '<span class="word-carefree">CAREFREE</span>');
    processedText = processedText.replace(/\bUNBURDENED\b/g, '<span class="word-unburdened">UNBURDENED</span>');
    processedText = processedText.replace(/\bWOES\b/g, '<span class="word-woes">WOES</span>');
    processedText = processedText.replace(/\bWRINKLES\b/g, '<span class="word-wrinkles">WRINKLES</span>');
    processedText = processedText.replace(/\bBASK\b/g, '<span class="word-bask">BASK</span>');
    
    // If we added any word highlighting, render as HTML
    if (processedText !== text) {
      // Also handle bold formatting in HTML
      processedText = processedText.replace(/\*\*([^*]+)\*\*/g, '<span style="font-weight: bold; color: #00ff00; text-shadow: 0 0 10px #00ff00, 0 0 20px #00ff00; animation: pulse 2s infinite;">$1</span>');
      return <span dangerouslySetInnerHTML={{ __html: processedText }} />;
    }
    
    // Fallback: Split by **text** pattern and render with bold highlights
    const parts = text.split(/(\*\*[^*]+\*\*)/);
    
    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        const boldText = part.slice(2, -2);
        return (
          <span 
            key={index} 
            style={{ 
              fontWeight: 'bold', 
              color: '#00ff00', 
              textShadow: '0 0 10px #00ff00, 0 0 20px #00ff00',
              animation: 'pulse 2s infinite'
            }}
          >
            {boldText}
          </span>
        );
      }
      return part;
    });
  };

  // Helper function to play sounds with mobile-friendly handling
  const playSound = async (audioRef, duration = null, fadeOutDuration = 0, forcePlay = false) => {
    // Skip audio on mobile if not explicitly enabled, unless forcePlay is true
    if (isMobile && !audioEnabled && !forcePlay) {
      return;
    }
    
    try {
      const audio = audioRef.current;
      if (!audio) return;
      
      audio.currentTime = 0;
      
      const playPromise = audio.play();
      
      if (playPromise !== undefined) {
        playPromise.then(() => {
          if (duration) {
            // If duration is specified, start fade out before stopping
            setTimeout(() => {
              if (fadeOutDuration > 0) {
                // Fade out effect
                const initialVolume = audio.volume;
                const fadeSteps = 20;
                const fadeStepDuration = fadeOutDuration / fadeSteps;
                const volumeStep = initialVolume / fadeSteps;
                
                let currentStep = 0;
                
                const fadeInterval = setInterval(() => {
                  currentStep++;
                  const newVolume = initialVolume - (volumeStep * currentStep);
                  audio.volume = Math.max(0, newVolume);
                  
                  if (currentStep >= fadeSteps) {
                    clearInterval(fadeInterval);
                    audio.pause();
                    audio.volume = initialVolume;
                  }
                }, fadeStepDuration);
              } else {
                audio.pause();
              }
            }, duration);
          }
        }).catch(error => {
          console.log('Audio playback failed:', error);
          // Don't retry on mobile to avoid delays unless forcePlay is true
          if (!isMobile || forcePlay) {
            setTimeout(() => {
              audio.play().catch(retryError => {
                console.log('Retry audio playback failed:', retryError);
              });
            }, 100);
          }
        });
      }
    } catch (err) {
      console.log('Audio playback error:', err);
    }
  };

  // Update the video reveal animation effect with mobile compatibility
  useEffect(() => {
    if (stage === 'video' && videoStage === 1) {
      setLastStage('video');
      
      // Trigger visual effects
      setShowDigitalRain(true);
      setShowMatrixRain(true);
      triggerScreenShake();
      
      // Create particle explosion at center
      setTimeout(() => {
        const centerX = window.innerWidth / 2;
        const centerY = window.innerHeight / 2;
        createParticleExplosion(centerX, centerY);
      }, 500);
      
      // Play reveal sound - force it to play even on mobile since it's part of the key experience
      const playRevealSound = async () => {
        try {
          if (revealSoundRef.current) {
            console.log('🎵 Attempting to play reveal sound (angel choir)');
            console.log('Audio state:', {
              paused: revealSoundRef.current.paused,
              readyState: revealSoundRef.current.readyState,
              networkState: revealSoundRef.current.networkState,
              volume: revealSoundRef.current.volume,
              muted: revealSoundRef.current.muted
            });

            // Ensure audio is not muted and reset position
            revealSoundRef.current.muted = false;
            revealSoundRef.current.currentTime = 0;
            revealSoundRef.current.volume = isMobile ? 0.5 : 0.7; // Lower volume on mobile

            // Play the audio
            const playPromise = revealSoundRef.current.play();
            if (playPromise !== undefined) {
              await playPromise;
              console.log('✅ Reveal sound (angel choir) playing successfully');
            }

            // Fade out after 2 seconds
            setTimeout(() => {
              const audio = revealSoundRef.current;
              if (audio && !audio.paused) {
                const initialVolume = audio.volume;
                const fadeSteps = 20;
                const fadeStepDuration = 500 / fadeSteps; // 500ms fade out
                const volumeStep = initialVolume / fadeSteps;
                
                let currentStep = 0;
                
                const fadeInterval = setInterval(() => {
                  currentStep++;
                  const newVolume = initialVolume - (volumeStep * currentStep);
                  audio.volume = Math.max(0, newVolume);
                  
                  if (currentStep >= fadeSteps) {
                    clearInterval(fadeInterval);
                    audio.pause();
                    audio.volume = initialVolume; // Reset volume for next play
                  }
                }, fadeStepDuration);
              }
            }, 2000);
          }
        } catch (err) {
          console.error('❌ Reveal sound failed to play:', err.name, err.message);
          console.log('Error details:', {
            errorName: err.name,
            errorMessage: err.message,
            audioReadyState: revealSoundRef.current?.readyState,
            audioNetworkState: revealSoundRef.current?.networkState
          });

          // Try one more time on mobile after a short delay
          if (isMobile) {
            setTimeout(async () => {
              try {
                if (revealSoundRef.current) {
                  console.log('🔄 Retrying reveal sound on mobile...');
                  revealSoundRef.current.muted = false;
                  const retryPromise = revealSoundRef.current.play();
                  if (retryPromise !== undefined) {
                    await retryPromise;
                    console.log('✅ Reveal sound retry successful');
                  }
                  
                  // Also add fade out to retry
                  setTimeout(() => {
                    const audio = revealSoundRef.current;
                    if (audio && !audio.paused) {
                      const initialVolume = audio.volume;
                      const fadeSteps = 20;
                      const fadeStepDuration = 500 / fadeSteps;
                      const volumeStep = initialVolume / fadeSteps;
                      
                      let currentStep = 0;
                      
                      const fadeInterval = setInterval(() => {
                        currentStep++;
                        const newVolume = initialVolume - (volumeStep * currentStep);
                        audio.volume = Math.max(0, newVolume);
                        
                        if (currentStep >= fadeSteps) {
                          clearInterval(fadeInterval);
                          audio.pause();
                          audio.volume = initialVolume;
                        }
                      }, fadeStepDuration);
                    }
                  }, 2000);
                }
              } catch (retryErr) {
                console.error('❌ Reveal sound retry also failed:', retryErr.name, retryErr.message);
                console.log('Final error details:', {
                  errorName: retryErr.name,
                  errorMessage: retryErr.message,
                  userAgent: navigator.userAgent,
                  isMobile: isMobile,
                  audioEnabled: audioEnabled
                });
              }
            }, 500);
          }
        }
      };
      
      playRevealSound();

      const growInterval = setInterval(() => {
        setRomanSize(prev => {
          if (prev >= 180) {
            clearInterval(growInterval);
            setTimeout(() => {
              setVideoStage(2);
              setShowDigitalRain(false); // Stop digital rain when video loads
              setShowMatrixRain(false); // Stop intense matrix rain when video loads
              console.log('Setting backgroundMatrix to true'); // Debug log
              setBackgroundMatrix(true); // Start subtle background matrix
            }, isMobile ? 1000 : 1500); // Faster on mobile
            return 180;
          }
          return prev + (isMobile ? 3 : 2); // Faster animation on mobile
        });
      }, isMobile ? 20 : 25); // Faster interval on mobile
      
      return () => clearInterval(growInterval);
    }
  }, [stage, videoStage, isMobile]);
  
  // Update the video watched handler
  const handleVideoWatched = (buttonElement = null) => {
    playTerminalSound('yes'); // Play terminal sound first
    playSound(chachingSoundRef, null, 0, true); // Force play on mobile
    
    // Add green glitch effect to the RSVP button
    if (buttonElement) {
      triggerGlitchEffect(buttonElement, 'green');
    }
    
    setVideoWatched(true);
  };
  
  // Modify the video stage effect to show RSVP form after video plays
  useEffect(() => {
    if (stage === 'video' && videoStage === 2) {
      // Shorter delay on mobile since attention span is shorter
      const timeout = setTimeout(() => {
        setShowRSVPForm(true);
      }, isMobile ? 5000 : 10000);
      
      return () => clearTimeout(timeout);
    }
  }, [stage, videoStage, isMobile]);
  
  // Process user's typed response with mobile improvements
  const handleUserInputSubmit = (e) => {
    e.preventDefault();
    
    if (!waitingForInput || !userInput.trim()) return;
    
    const input = userInput.trim().toLowerCase();
    
    // Process the input - glitch effect will happen on submit button
    if (input === 'yes' || input === 'y') {
      playTerminalSound('yes');
      handleOptionClick('yes');
    } else if (input === 'no' || input === 'n') {
      playTerminalSound('no');
      handleOptionClick('no');
    } else {
      // Handle invalid input
      playTerminalSound('error');
      
      const newResponses = [...responses];
      newResponses.push(`> ${input.toUpperCase()}`);
      newResponses.push('SYSTEM: I DON\'T UNDERSTAND, YES OR NO:');
      setResponses(newResponses);
    }
    
    // Clear input field
    setUserInput('');
  };
  
  // Handle button clicks for different choices with mobile-optimized timing
  const handleOptionClick = (option, clickedElement = null) => {
    // Play terminal sound effect
    playTerminalSound(option === 'yes' ? 'yes' : 'no');

    // Add visual effects for dramatic responses - especially important for mobile users with muted sound
    if (option === 'yes') {
      // Green glitch for positive responses
      if (clickedElement) {
        triggerGlitchEffect(clickedElement, 'green');
      }

      // Also apply to current question for dramatic effect
      const questionElement = document.querySelector('.terminal-prompt');
      if (questionElement) {
        setTimeout(() => triggerGlitchEffect(questionElement, 'green'), 100);
      }

      // Increment score for correct answer
      setScore(prev => prev + 1);
    } else if (option === 'no') {
      // Red glitch for negative responses
      if (clickedElement) {
        triggerGlitchEffect(clickedElement, 'red');
      }

      // Also apply to current question
      const questionElement = document.querySelector('.terminal-prompt');
      if (questionElement) {
        setTimeout(() => triggerGlitchEffect(questionElement, 'red'), 100);
      }
    }

    setShowOptions(false);
    setWaitingForInput(false);

    const delay = isMobile ? 800 : 1000;

    // Linear progression through questions
    if (stage === 'rich') {
      setTimeout(() => {
        setStage('wealthy');
        setQuestionNumber(2);
        setQuestion(visitorName ? `${visitorName.toUpperCase()}, DO YOU WANT TO BE WEALTHY?` : 'DO YOU WANT TO BE WEALTHY?');
        setShowOptions(true);
        setWaitingForInput(true);
        setLastStage('wealthy');

        // Focus on input when ready (desktop only)
        if (inputRef.current && !isMobile) {
          setTimeout(() => {
            try {
              inputRef.current.focus();
            } catch (e) {
              // Focus may fail, that's ok
            }
          }, 100);
        }
      }, delay);
    } else if (stage === 'wealthy') {
      setTimeout(() => {
        setStage('health');
        setQuestionNumber(3);
        setQuestion(visitorName ? `${visitorName.toUpperCase()}, DO YOU WANT TO BE OVERLY FINANCIALLY SECURE SO YOU DON'T HAVE TO WORRY ABOUT CRIPPLING UNCERTAINTY, AND CAN BASK IN THE EVER YOUTHFUL SUNRISE OF CAREFREE HAPPINESS, UNBURDENED BY STRESS, WORRY, OR FINANCIAL WOES, WITH EVERYONE FINALLY SAYING HOW COOL YOU LOOK, OR THAT YOUR WRINKLES ARE UNWRINKLING, OR YOUR 💖 PARENTS 💖 SAYING THEY ARE PROUD OF YOU?` : 'DO YOU WANT TO BE OVERLY FINANCIALLY SECURE SO YOU DON\'T HAVE TO WORRY ABOUT CRIPPLING UNCERTAINTY, AND CAN BASK IN THE EVER YOUTHFUL SUNRISE OF CAREFREE HAPPINESS, UNBURDENED BY STRESS, WORRY, OR FINANCIAL WOES, WITH EVERYONE FINALLY SAYING HOW COOL YOU LOOK, OR THAT YOUR WRINKLES ARE UNWRINKLING, OR YOUR 💖 PARENTS 💖 SAYING THEY ARE PROUD OF YOU?');
        setShowOptions(true);
        setWaitingForInput(true);
        setLastStage('health');

        // Focus on input when ready (desktop only)
        if (inputRef.current && !isMobile) {
          setTimeout(() => {
            try {
              inputRef.current.focus();
            } catch (e) {
              // Focus may fail, that's ok
            }
          }, 100);
        }
      }, delay);
    } else if (stage === 'health') {
      setTimeout(() => {
        const updatedResponses = [...responses];
        updatedResponses.push(visitorName ? `SYSTEM: TIME FOR YOUR FUTURE, ${glitchifyName(visitorName)}` : 'SYSTEM: TIME FOR YOUR FUTURE');
        updatedResponses.push('SYSTEM: INITIATING MARKET APPLICATION...');
        setResponses(updatedResponses);
        setQuestion('');
        setTypedQuestion('');
        setStage('loading');
        setShowOptions(false);
        setWaitingForInput(false);
        setLastStage('loading');

        // Trigger Matrix rain for dramatic effect
        setShowMatrixRain(true);
        setTimeout(() => {
          setShowMatrixRain(false);
        }, 5000);

        // Wait longer on mobile before showing loading animation
        setTimeout(() => {
          const responsesWithLoading = [...updatedResponses];
          responsesWithLoading.push('LOADING_ANIMATION');
          setResponses(responsesWithLoading);

          // Start the loading sequence
          startLoadingSequence(() => {
            setStage('video');
            setVideoStage(1);
            setLastStage('video');
          });
        }, isMobile ? 2000 : 1000);
      }, delay);
    }
  };
  
  // Handle RSVP form input changes
  const handleRSVPChange = (e) => {
    const { name, value } = e.target;
    setRsvpData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Update the RSVP submit handler with better mobile error handling
  const handleRSVPSubmit = (e) => {
    e.preventDefault();

    const form = e.target;
    const formData = new FormData(form);

    // Show loading state for mobile users
    const submitButton = form.querySelector('button[type="submit"]');
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = 'SUBMITTING...';
    }

    fetch("/", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams(formData).toString()
    })
    .then(() => {
      playSound(chachingSoundRef, null, 0, true); // Force play on mobile
      setRsvpSubmitted(true);
    })
    .catch(error => {
      console.error("Form submission error:", error);
      if (isMobile) {
        // More user-friendly error on mobile
        alert("Submission failed. Please check your connection and try again.");
      } else {
        alert("There was an error submitting your RSVP. Please try again.");
      }
    })
    .finally(() => {
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.textContent = 'SUBMIT RSVP';
      }
    });
  };
  
  // KOMOK logo component
  const KomokLogo = () => (
    <div className="komok-logo">
      <div className="komok-text">
        KOMO<span className="mirrored-k">K</span>
      </div>
    </div>
  );
  
  // Handle initial interaction with better mobile support
  const handleStart = async (buttonElement = null) => {
    setHasInteracted(true);

    // Play terminal click sound
    playTerminalSound('click');

    // Add green glitch effect to start button
    if (buttonElement) {
      triggerGlitchEffect(buttonElement, 'green');
    }

    // Unlock ALL audio on mobile by playing and immediately pausing
    // This is required because mobile browsers need user gesture to unlock EACH audio element
    const unlockAudio = async (audioRef) => {
      try {
        if (audioRef.current) {
          audioRef.current.muted = true; // Mute during unlock
          const playPromise = audioRef.current.play();
          if (playPromise !== undefined) {
            await playPromise;
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            audioRef.current.muted = false; // Unmute after unlock
          }
        }
      } catch (err) {
        console.log('Audio unlock failed:', err);
      }
    };

    // Unlock all audio elements on mobile
    if (isMobile) {
      await Promise.all([
        unlockAudio(startupSoundRef),
        unlockAudio(revealSoundRef),
        unlockAudio(chachingSoundRef)
      ]);

      // Also resume AudioContext if it exists (for Web Audio API sounds)
      if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
        try {
          await audioContextRef.current.resume();
          console.log('AudioContext resumed on mobile');
        } catch (err) {
          console.log('AudioContext resume failed:', err);
        }
      }
    }

    // Try to enable audio and play startup sound
    try {
      if (startupSoundRef.current) {
        await startupSoundRef.current.play();
        setAudioEnabled(true);
        setStage('boot');
      }
    } catch (err) {
      console.log('Audio playback failed, continuing without sound:', err);
      // Continue with boot sequence even if sound fails
      setStage('boot');
    }
  };
  
  // Touch event handlers for better mobile interaction
  const handleTouchStart = (callback) => (e) => {
    // Prevent double-tap zoom on mobile
    e.preventDefault();
    callback();
  };

  return (
    <>
      <style>{soundWaveStyles}</style>
      {/* TV Boot Effect Overlay */}
      {showTvBoot && (
        <div className="tv-boot-overlay">
          <div className="tv-static"></div>
          <div className="tv-turn-on-effect"></div>
        </div>
      )}
      {/* Add preloaded iframes as soon as component mounts */}
      {/* <PreloadedIframes /> */}
      <div className={`terminal-container ${!tvBootComplete ? 'booting' : ''}`}>
        {!hasInteracted ? (
          <div className="terminal-body" style={{
            display: 'flex',
            justifyContent: 'safe center',
            alignItems: 'center',
            minHeight: '100%',
            flexDirection: 'column',
            gap: '15px',
            padding: '20px 15px'
          }}>
            <div className="terminal-title" style={{ fontSize: isMobile ? '20px' : '24px', fontWeight: 'bold' }}>MARKET APPLICATION V3</div>
            <div style={{
              color: '#00ff00',
              fontSize: isMobile ? '18px' : '20px',
              marginTop: '-10px',
              marginBottom: '10px',
              textAlign: 'center'
            }}>
              {visitorName ? (
                <>Hello <span className="visitor-name-glitch">{visitorName}</span>. <br /> We've Been Waiting For You. <br /> Let's See What You're Worth.</>
              ) : (
                <>Hello. <br />We've Been Waiting For You. <br /> Let's See What You're Worth.</>
              )}
            </div>
            <div style={{
              color: '#00ff00',
              fontSize: isMobile ? '12px' : '14px',
              opacity: 0.8,
              fontWeight: 'bold',
              marginTop: '0px',
              marginBottom: '10px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <div className="sound-waves">
                <div className="wave-line">~~~~~~~~</div>
                <div className="wave-line">~~~~~~~~</div>
                <div className="wave-line">~~~~~~~~</div>
              </div>
              <span>SOUND ON</span>
              <div className="sound-waves">
                <div className="wave-line">~~~~~~~~</div>
                <div className="wave-line">~~~~~~~~</div>
                <div className="wave-line">~~~~~~~~</div>
              </div>
            </div>

            <button
              onClick={(e) => handleStart(e.target)}
              onTouchStart={isMobile ? handleTouchStart(() => handleStart()) : undefined}
              className="terminal-option yes start-button-glow"
              style={{
                padding: isMobile ? '18px 35px' : '15px 30px',
                fontSize: isMobile ? '20px' : '18px',
                minHeight: isMobile ? '60px' : 'auto',
                touchAction: 'manipulation'
              }}
            >
              START
            </button>

            <a
              href="/login"
              style={{
                color: 'rgba(0,255,0,0.5)',
                fontSize: isMobile ? '12px' : '13px',
                textDecoration: 'none',
                borderBottom: '1px solid rgba(0,255,0,0.3)',
                paddingBottom: '2px',
                paddingTop: '4px',
                marginTop: '4px',
                transition: 'color 0.2s, border-color 0.2s'
              }}
              onMouseEnter={e => { e.target.style.color = '#00ff00'; e.target.style.borderColor = '#00ff00'; }}
              onMouseLeave={e => { e.target.style.color = 'rgba(0,255,0,0.5)'; e.target.style.borderColor = 'rgba(0,255,0,0.3)'; }}
            >
              Looking to enter? Go here →
            </a>

          </div>
        ) : (
          <>
            {/* Matrix rain background effect - always show container when stage is video */}
            {(showMatrixRain || backgroundMatrix || stage === 'video') && <div className="matrix-rain"></div>}
            
            {/* Digital rain for loading effects */}
            {showDigitalRain && <div className="digital-rain"></div>}
            
            {/* Particle effects */}
            {showParticles && (
              <div className="particles-container">
                {particles.map(particle => (
                  <div
                    key={particle.id}
                    className="particle"
                    style={{
                      left: particle.x + 'px',
                      top: particle.y + 'px',
                      '--dx': particle.dx + 'px',
                      '--dy': particle.dy + 'px'
                    }}
                  />
                ))}
              </div>
            )}
            
            {/* Header is always visible */}
            <div className="terminal-header">
              <div className="terminal-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  fontSize: isMobile ? '16px' : '20px',
                  fontWeight: 'bold',
                  color: '#00ff00',
                  textShadow: '0 0 10px #00ff00',
                  border: '2px solid #00ff00',
                  padding: '1px 6px',
                  borderRadius: '4px',
                  backgroundColor: 'rgba(0, 255, 0, 0.05)',
                  boxShadow: '0 0 15px rgba(0, 255, 0, 0.3), inset 0 0 10px rgba(0, 255, 0, 0.1)'
                }}>
                  KOMO<span style={{ display: 'inline-block', transform: 'scaleX(-1)' }}>K</span>
                </div>
                <span>TERMINAL</span>
              </div>
              <div className="terminal-status">
                <span
                  className="status-indicator"
                  style={{
                    display: 'inline-block',
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    marginRight: '8px',
                    backgroundColor: stage === 'boot' ? '#ff0000' : '#00ff00',
                    boxShadow: stage === 'boot' ? '0 0 8px #ff0000' : '0 0 8px #00ff00',
                    animation: 'pulse 2s infinite'
                  }}
                ></span>
                <span style={{ color: stage === 'boot' ? '#ff0000' : '#00ff00' }}>
                  {stage === 'boot' ? 'OFFLINE' : 'ONLINE'}
                </span>
              </div>
            </div>

            {stage !== 'video' ? (
              <div className="terminal-body" ref={terminalRef}>
                {/* Always show boot sequence text, even after boot is complete */}
                <div className="boot-sequence" style={{ display: stage === 'boot' || bootComplete ? 'block' : 'none' }}>
                  <div>{renderBootSequenceText(fullText, visitorName)}</div>
                  {currentText && (
                    <div>
                      {currentText}
                      <span className={`cursor ${cursorVisible ? 'visible' : 'hidden'}`}>█</span>
                    </div>
                  )}
                </div>
                
                {/* Only show dialogue after boot is complete */}
                {stage !== 'boot' && (
                  <div className="dialogue-sequence">
                    {/* Score counter - only show during questionnaire stages */}
                    {(stage === 'rich' || stage === 'wealthy' || stage === 'health') && questionNumber > 0 && (
                      <div style={{
                        fontFamily: 'monospace',
                        fontSize: isMobile ? '14px' : '16px',
                        color: '#00ff00',
                        textShadow: '0 0 10px #00ff00',
                        marginBottom: '10px',
                        letterSpacing: '2px',
                        fontWeight: 'bold'
                      }}>
                        CORRECT ANSWERS: {score}/3
                      </div>
                    )}
                    <div className="terminal-prompt" style={{
                      whiteSpace: 'normal',
                      overflow: 'visible',
                      fontSize: isMobile ? (question.length > 150 ? '14px' : '16px') : 'inherit',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      maxWidth: '100%',
                      width: '100%'
                    }}>
                      {renderFormattedText(typedQuestion)}
                      {questionTyping && <span className="cursor" style={{ marginLeft: '2px' }}>█</span>}
                    </div>
                    
                    {responses.map((response, index) => (
                      <div key={index} className={`terminal-response ${response.startsWith('SYSTEM:') ? 'system' : 'user'}`}>
                        {renderFormattedText(response)}
                      </div>
                    ))}
                    
                    {showOptions && (
                      <div className="terminal-options" style={{
                        flexDirection: isMobile ? 'column' : 'row',
                        gap: isMobile ? '15px' : '20px',
                        alignItems: isMobile ? 'stretch' : 'flex-start'
                      }}>
                        <button
                          className="terminal-option yes"
                          onClick={(e) => handleOptionClick('yes', e.target)}
                          onTouchStart={isMobile ? handleTouchStart(() => handleOptionClick('yes')) : undefined}
                          style={{
                            minHeight: isMobile ? '50px' : 'auto',
                            padding: isMobile ? '15px 30px' : '10px 30px',
                            fontSize: isMobile ? '18px' : '16px',
                            touchAction: 'manipulation'
                          }}
                        >
                          YES
                        </button>

                        <button
                          className="terminal-option no"
                          onClick={(e) => handleOptionClick('no', e.target)}
                          onTouchStart={isMobile ? handleTouchStart(() => handleOptionClick('no')) : undefined}
                          style={{
                            minHeight: isMobile ? '50px' : 'auto',
                            padding: isMobile ? '15px 30px' : '10px 30px',
                            fontSize: isMobile ? '18px' : '16px',
                            touchAction: 'manipulation'
                          }}
                        >
                          NO
                        </button>
                      </div>
                    )}
                    
                    {/* Add a form for text input with mobile improvements */}
                    {waitingForInput && !isMobile && (
                      <form onSubmit={handleUserInputSubmit} className="terminal-input-form">
                        <div className="terminal-line" style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: isMobile ? '8px' : '6px'
                        }}>
                          <span className="terminal-prompt-icon">$&gt;</span>
                          <input
                            ref={inputRef}
                            type="text"
                            value={userInput}
                            onChange={(e) => setUserInput(e.target.value)}
                            className="terminal-text-input"
                            placeholder={isMobile ? "TYPE YES/NO" : "TYPE/CLICK YES/NO"}
                            autoFocus={!isMobile}
                            style={{
                              fontSize: isMobile ? '16px' : '14px',
                              minHeight: isMobile ? '44px' : 'auto',
                              padding: isMobile ? '12px' : '8px',
                              flex: 1
                            }}
                          />
                          <button
                            type="submit"
                            className="terminal-submit-btn"
                            disabled={!userInput.trim()}
                            onClick={(e) => {
                              // Add subtle glitch effect based on input value
                              const input = userInput.trim().toLowerCase();
                              if (input === 'yes' || input === 'y') {
                                triggerGlitchEffect(e.target, 'green');
                              } else if (input === 'no' || input === 'n') {
                                triggerGlitchEffect(e.target, 'red');
                              } else if (input) {
                                triggerGlitchEffect(e.target, 'default');
                              }
                            }}
                            style={{
                              backgroundColor: !userInput.trim() ? 'var(--terminal-dark)' : 'var(--terminal-header)',
                              border: '1px solid var(--terminal-highlight)',
                              color: !userInput.trim() ? 'var(--terminal-border)' : 'var(--terminal-highlight)',
                              padding: isMobile ? '12px 16px' : '8px 12px',
                              fontSize: isMobile ? '14px' : '12px',
                              fontFamily: 'var(--terminal-font)',
                              cursor: !userInput.trim() ? 'not-allowed' : 'pointer',
                              minHeight: isMobile ? '44px' : 'auto',
                              touchAction: 'manipulation',
                              transition: 'all 0.2s',
                              fontWeight: 'bold',
                              opacity: !userInput.trim() ? 0.5 : 1
                            }}
                            onTouchStart={isMobile ? handleTouchStart(() => {}) : undefined}
                          >
                            {isMobile ? 'GO' : 'ENTER'}
                          </button>
                        </div>
                      </form>
                    )}
                    
                    {!waitingForInput && (
                      <div className="terminal-line">
                        <span className="terminal-prompt-icon">$&gt;</span>
                        <span className={`cursor-end ${cursorVisible ? 'visible' : 'hidden'}`}>█</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="video-container">
                {videoStage === 1 ? (
                  <div className="roman-numeral-container">
                    <div className="roman-numeral" style={{
                      fontSize: `${romanSize}px`,
                      fontFamily: 'Courier New, monospace',
                      fontWeight: 'bold',
                      letterSpacing: `${romanSize * 0.05}px`,
                      color: '#00ff00',
                      textShadow: '0 0 20px #00ff00, 0 0 40px #00ff00, 0 0 60px #00ff00',
                      whiteSpace: 'nowrap'
                    }}>
                      |||
                    </div>
                  </div>
                ) : (
                  <div className="video-reveal">
                    {/* Final recap CTA — party already happened */}
                    <a
                      href="/videos/?tab=party-video"
                      style={{
                        display: 'block',
                        textAlign: 'center',
                        textDecoration: 'none',
                        color: '#00ff00',
                        border: '2px solid #00ff00',
                        background: 'rgba(0, 255, 0, 0.06)',
                        padding: isMobile ? '12px 14px' : '14px 18px',
                        margin: isMobile ? '10px auto 18px' : '10px auto 24px',
                        maxWidth: isMobile ? '95%' : '500px',
                        fontFamily: 'monospace',
                        fontSize: isMobile ? '13px' : '15px',
                        fontWeight: 'bold',
                        letterSpacing: '0.5px',
                        textShadow: '0 0 8px #00ff00',
                        boxShadow: '0 0 12px rgba(0, 255, 0, 0.3)',
                        cursor: 'pointer'
                      }}
                    >
                      WANT TO SEE THE PARTY? CHECK OUT /VIDEOS &gt;
                    </a>

                    {/* Invitation message with glitched name */}
                    <div style={{
                      fontSize: isMobile
                        ? (visitorName && visitorName.length > 8
                          ? '13px'
                          : visitorName && visitorName.length > 5
                            ? '15px'
                            : '17px')
                        : '32px',
                      fontWeight: 'bold',
                      color: '#00ff00',
                      textAlign: 'center',
                      marginBottom: '30px',
                      textShadow: '0 0 10px #00ff00, 0 0 20px #00ff00',
                      animation: 'pulse 2s infinite',
                      fontFamily: 'monospace',
                      whiteSpace: 'normal',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      maxWidth: '100%',
                      width: '100%',
                      padding: isMobile ? '0 10px' : '0',
                      boxSizing: 'border-box',
                      marginTop: '20px'
                    }}>
                      {videoInvitationMessage}
                    </div>

                    {/* Vimeo embed with better mobile handling */}
                    <div className="compact-video">
                      <div style={{
                        padding: '56.25% 0 0 0', // Standard 16:9 aspect ratio
                        position: 'relative'
                      }}>
                        <iframe 
                          src={getCurrentVideoUrl('hype1')} 
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            border: '2px solid var(--terminal-highlight)'
                          }}
                          frameBorder="0" 
                          allow="autoplay; fullscreen; picture-in-picture" 
                          allowFullScreen
                          title="Invitation Video"
                          data-video="hype1"
                          onLoad={() => handleVideoLoad('hype1')}
                          onError={() => handleVideoError('hype1')}
                        />
                        <VideoLoadingIndicator videoKey="hype1" />
                      </div>
                    </div>

                    <div className="date-display" style={{ marginTop: '15px' }}>
                      <div style={{
                        border: '1px solid var(--terminal-highlight)',
                        padding: isMobile ? '12px' : '10px',
                        marginBottom: '12px',
                        backgroundColor: 'rgba(0, 255, 0, 0.03)',
                        fontFamily: 'monospace',
                        fontSize: isMobile ? '13px' : '12px',
                        lineHeight: '1.6'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '4px' }}>
                          <span style={{ color: 'var(--terminal-highlight)', opacity: 0.6 }}>DATE</span>
                          <span style={{ fontWeight: 'bold' }}>MARCH 20, 2026 — 6:45 PM</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '4px' }}>
                          <span style={{ color: 'var(--terminal-highlight)', opacity: 0.6 }}>LOCATION</span>
                          <span style={{ fontWeight: 'bold' }}>VANCOUVER, BC</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '4px' }}>
                          <span style={{ color: 'var(--terminal-highlight)', opacity: 0.6 }}>DRESS CODE</span>
                          <span style={{ fontWeight: 'bold' }}>DRESS FOR BUSINESS.</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                          <span style={{ color: 'var(--terminal-highlight)', opacity: 0.6 }}>BRING</span>
                          <span style={{ fontWeight: 'bold' }}>CHARGED PHONE, $20, BE ON TIME (or early)</span>
                        </div>
                      </div>

                      <div className="date-label" style={{ textAlign: 'center', fontWeight: 'bold', fontSize: isMobile ? '12px' : '14px' }}>
                        WATCH THE VIDEO ABOVE FOR <br />FULL DETAILS THEN RSVP.
                      </div>
                    </div>

                    {!videoWatched && !rsvpSubmitted && (
                      <div className="watch-button-container">
                        <button
                          onClick={(e) => handleVideoWatched(e.target)}
                          onTouchStart={isMobile ? handleTouchStart(() => handleVideoWatched()) : undefined}
                          className="rsvp-button"
                          style={{
                            padding: isMobile ? '12px 20px' : '5px 10px',
                            fontSize: isMobile ? '14px' : '12px',
                            minHeight: isMobile ? '50px' : 'auto',
                            touchAction: 'manipulation'
                          }}
                          disabled={videoLoadingStates['hype1'] === 'loading'}
                        >
                          {videoLoadingStates['hype1'] === 'loading'
                            ? (isMobile ? 'LOADING...' : 'VIDEO LOADING...')
                            : (isMobile ? 'RSVP AFTER WATCHING' : 'CLICK HERE TO RSVP AFTER VIDEO')
                          }
                        </button>
                      </div>
                    )}

                    {/* Only show hr when RSVP form is visible */}
                    {videoWatched && <hr style={{border: '1px solid var(--terminal-highlight)', width: '100%', margin: '10px 0'}} />}

                    {/* RSVP Form with mobile improvements */}
                    {videoWatched && !rsvpSubmitted && (
                      <div className="rsvp-form" style={{
                        padding: isMobile ? '15px' : '8px',
                        fontSize: isMobile ? '14px' : '12px'
                      }}>
                        <h2 style={{ fontSize: isMobile ? '18px' : '16px' }}>RSVP TO THE PARTY III</h2>
                        <p style={{ fontSize: isMobile ? '14px' : '12px', opacity: 0.7, marginTop: '0' }}>Text us if you're bringing a guest or have any questions.</p>
                        <form
                          name="rsvp-form"
                          method="post"
                          data-netlify="true"
                          onSubmit={handleRSVPSubmit}
                        >
                          {/* Required hidden field for Netlify */}
                          <input type="hidden" name="form-name" value="rsvp-form" />
                          
                          <div className="form-group">
                            <label style={{ fontSize: isMobile ? '14px' : '12px' }}>
                              DO YOU WANT TO BE A MARKET PARTICIPANT?
                            </label>
                            <select 
                              name="attending" 
                              required
                              style={{ 
                                padding: isMobile ? '8px' : '4px',
                                fontSize: isMobile ? '16px' : '12px',
                                minHeight: isMobile ? '44px' : 'auto'
                              }}
                            >
                              <option value="yes">YES</option>
                              <option value="no">NO</option>
                              <option value="maybe">MAYBE</option>
                            </select>
                          </div>
                          
                          <div className="form-group">
                            <label style={{ fontSize: isMobile ? '14px' : '12px' }}>
                              YOUR FULL NAME
                            </label>
                            <input
                              type="text"
                              name="name"
                              placeholder="ENTER YOUR NAME"
                              required
                              style={{
                                padding: isMobile ? '8px' : '4px',
                                fontSize: isMobile ? '16px' : '12px',
                                minHeight: isMobile ? '44px' : 'auto'
                              }}
                            />
                          </div>

                          <div className="rsvp-buttons">
                            <button 
                              type="submit" 
                              className="rsvp-button"
                              style={{ 
                                padding: isMobile ? '15px 25px' : '5px 10px',
                                fontSize: isMobile ? '16px' : '12px',
                                minHeight: isMobile ? '50px' : 'auto',
                                touchAction: 'manipulation'
                              }}
                            >
                              SUBMIT RSVP
                            </button>
                          </div>
                        </form>
                      </div>
                    )}

                    {/* Only show hr when success message is visible */}
                    {rsvpSubmitted && <hr style={{border: '1px solid var(--terminal-highlight)', width: '100%', margin: '10px 0'}} />}

                    {/* RSVP Success Message with mobile styling */}
                    {rsvpSubmitted && (
                      <div ref={rsvpSuccessRef} className="rsvp-success" style={{
                        padding: isMobile ? '12px' : '5px',
                        margin: isMobile ? '10px 0' : '5px 0',
                        scrollMarginTop: isMobile ? '20px' : '0'
                      }}>
                        <h2 style={{
                          fontSize: isMobile ? '18px' : '16px',
                          margin: isMobile ? '8px 0' : '5px 0'
                        }}>
                          RSVP RECEIVED
                        </h2>
                        <p style={{
                          margin: isMobile ? '5px 0' : '3px 0',
                          fontSize: isMobile ? '14px' : '12px'
                        }}>
                          PREPARE FOR III
                        </p>
                        <p style={{
                          margin: isMobile ? '5px 0' : '3px 0',
                          fontSize: isMobile ? '14px' : '12px'
                        }}>
                          WORK. WORK. WORK.
                        </p>
                      </div>
                    )}

                    {/* Past party videos section with mobile improvements - always shown */}
                    <div className="past-parties" style={{ 
                          marginTop: isMobile ? '15px' : '20px',
                          width: '100%', 
                          maxWidth: isMobile ? '95%' : '500px',
                          margin: '0 auto' 
                        }}>
                          <h2 style={{
                            color: 'var(--terminal-highlight)',
                            textAlign: 'center',
                            fontSize: isMobile ? '11px' : '14px',
                            margin: isMobile ? '8px 0' : '12px 0'
                          }}>
                            HERE WERE SOME OF THE PAST PARTIES:
                          </h2>

                          {/* Squid Game Video */}
                          <div className="past-party-video squid-game-video" style={{
                            marginBottom: isMobile ? '15px' : '20px',
                            maxWidth: isMobile ? '95%' : '700px',
                            margin: '0 auto',
                            position: 'relative'
                          }}>
                            <h3 style={{ 
                              color: '#ff006e',
                              fontSize: isMobile ? '12px' : '14px',
                              margin: isMobile ? '8px 0' : '10px 0',
                              textAlign: 'center'
                            }}>
                              PARTY I: SQUID GAME
                            </h3>
                            <div style={{ position: 'relative', padding: '56.25% 0 0 0' }}>
                              <iframe 
                                src="https://player.vimeo.com/video/1004378850?h=b69d7125c5&badge=0&autopause=0&player_id=0&app_id=58479" 
                                style={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  width: '100%',
                                  height: '100%',
                                  border: '2px solid #ff006e'
                                }}
                                frameBorder="0" 
                                allow="autoplay; fullscreen; picture-in-picture" 
                                allowFullScreen
                                title="Party I: Squid Game"
                                onLoad={() => console.log('Squid Game video loaded')}
                                onError={() => console.log('Squid Game video failed to load')}
                              />
                              
                              {/* Loading indicator for past videos */}
                              <div style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                zIndex: 1,
                                border: '2px solid #ff006e',
                                opacity: 0,
                                transition: 'opacity 0.3s ease',
                                pointerEvents: 'none'
                              }} className="past-video-loading">
                                <div style={{
                                  color: '#ff006e',
                                  fontSize: isMobile ? '12px' : '14px',
                                  marginBottom: '8px',
                                  textAlign: 'center'
                                }}>
                                  LOADING...
                                </div>
                                <div style={{
                                  width: '30px',
                                  height: '30px',
                                  border: '2px solid transparent',
                                  borderTop: '2px solid #ff006e',
                                  borderRadius: '50%',
                                  animation: 'spin 1s linear infinite'
                                }}></div>
                              </div>
                            </div>
                          </div>

                          {/* Hogwarts Video */}
                          <div className="past-party-video hogwarts-video" style={{
                            marginBottom: isMobile ? '15px' : '20px',
                            maxWidth: isMobile ? '95%' : '700px',
                            margin: '0 auto',
                            position: 'relative'
                          }}>
                            <h3 style={{ 
                              color: '#9c7c38',
                              fontSize: isMobile ? '12px' : '14px',
                              margin: isMobile ? '8px 0' : '10px 0',
                              textAlign: 'center'
                            }}>
                              PARTY II: THE HOGWARTS EXPERIENCE
                            </h3>
                            <div style={{ position: 'relative', padding: '56.25% 0 0 0' }}>
                              <iframe 
                                src="https://player.vimeo.com/video/1076582009?h=68cc18f98b&badge=0&autopause=0&player_id=0&app_id=58479" 
                                style={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  width: '100%',
                                  height: '100%',
                                  border: '2px solid #9c7c38'
                                }}
                                frameBorder="0" 
                                allow="autoplay; fullscreen; picture-in-picture" 
                                allowFullScreen
                                title="Party II: Hogwarts"
                                onLoad={() => console.log('Hogwarts video loaded')}
                                onError={() => console.log('Hogwarts video failed to load')}
                              />
                              
                              {/* Loading indicator for past videos */}
                              <div style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                zIndex: 1,
                                border: '2px solid #9c7c38',
                                opacity: 0,
                                transition: 'opacity 0.3s ease',
                                pointerEvents: 'none'
                              }} className="past-video-loading">
                                <div style={{
                                  color: '#9c7c38',
                                  fontSize: isMobile ? '12px' : '14px',
                                  marginBottom: '8px',
                                  textAlign: 'center'
                                }}>
                                  LOADING...
                                </div>
                                <div style={{
                                  width: '30px',
                                  height: '30px',
                                  border: '2px solid transparent',
                                  borderTop: '2px solid #9c7c38',
                                  borderRadius: '50%',
                                  animation: 'spin 1s linear infinite'
                                }}></div>
                              </div>
                            </div>
                          </div>

                          {/* KOOK Video */}
                          <div className="past-party-video kook-video" style={{
                            marginBottom: isMobile ? '15px' : '20px',
                            maxWidth: isMobile ? '95%' : '700px',
                            margin: '0 auto',
                            position: 'relative'
                          }}>
                            <h3 style={{ 
                              color: 'rgb(81, 67, 237)',
                              fontSize: isMobile ? '12px' : '14px',
                              margin: isMobile ? '8px 0' : '10px 0',
                              textAlign: 'center'
                            }}>
                              PARTY II.5: KOOK
                            </h3>
                            <div style={{ position: 'relative', padding: '56.25% 0 0 0' }}>
                              <iframe 
                                src="https://player.vimeo.com/video/1002483563?h=414573fb2f&badge=0&autopause=0&player_id=0&app_id=58479"
                                style={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  width: '100%',
                                  height: '100%',
                                  border: '2px solid rgb(81, 67, 237)'
                                }}
                                frameBorder="0"
                                allow="autoplay; fullscreen; picture-in-picture" 
                                allowFullScreen
                                title="Party 2.5: KOOK"
                                onLoad={() => console.log('KOOK video loaded')}
                                onError={() => console.log('KOOK video failed to load')}
                              />
                              
                              {/* Loading indicator for past videos */}
                              <div style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                zIndex: 1,
                                border: '2px solid rgb(81, 67, 237)',
                                opacity: 0,
                                transition: 'opacity 0.3s ease',
                                pointerEvents: 'none'
                              }} className="past-video-loading">
                                <div style={{
                                  color: 'rgb(81, 67, 237)',
                                  fontSize: isMobile ? '12px' : '14px',
                                  marginBottom: '8px',
                                  textAlign: 'center'
                                }}>
                                  LOADING...
                                </div>
                                <div style={{
                                  width: '30px',
                                  height: '30px',
                                  border: '2px solid transparent',
                                  borderTop: '2px solid rgb(81, 67, 237)',
                                  borderRadius: '50%',
                                  animation: 'spin 1s linear infinite'
                                }}></div>
                              </div>
                            </div>
                          </div>
                        </div>
                  </div>
                )}
              </div>
            )}
            
            <div className="terminal-footer">
              <div className="ticker-tape">
                <div className="ticker-content" style={{
                  fontSize: isMobile ? '12px' : '14px'
                }}>
                  III • MARKET PARTICIPANTS WANTED • GET RICH OR DIE TRYING • UNDER A BRIDGE OR THE PENTHOUSE, THERE IS NO INBETWEEN • III • MONEY CANNOT BUY HAPPINESS BUT IT CAN BUY EVERYTHING ELSE • III •  MARKETS ARE IRRATIONAL BUT YOU SHOULDN'T BE • BET MORE • III • THERE ARE RISKS OF COURSE, BUT IMPORTANTLY THERE ARE ALSO REWARDS •  III • 
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default TerminalInvite;